import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { setOnboardingActor } from './components/onboarding/utils/api';
import { supabase, signOut, getSession } from './lib/supabase';
import { fetchAllTasks } from './lib/supabaseTasks';
import { parseRecoveryFromUrl, RECOVERY_VERIFY_FAILED_MSG } from './lib/recoveryLink';
import { spaNavigate } from './lib/spaNavigate';
import { signIntoFirebase, signOutOfFirebase, isFirebaseSignedIn } from './lib/firebase';
import { requestNotificationPermission, refreshPushSubscription, onForegroundMessage, isPushSupported } from './lib/firebase';
import { initPostHog, identifyUser, resetUser, trackEvent } from './lib/posthog';
import { Music, Plus, Calendar, ArrowRight, X, Trash2, Loader2, RotateCcw, Building2 } from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Toaster } from '@/components/ui/sonner';
import { pushUndo } from './lib/undoStack';
import { deleteRecurringSeries, restoreRecurringSeries } from './lib/recurringTasks';
import { isDesktopApp, onDesktopNotificationClick, takePendingDesktopDeepLink, getDesktopNotificationPermission } from './lib/desktopBridge';
import { installConnectionMonitor } from './lib/connectionMonitor';
import { initClientTelemetry } from './lib/clientTelemetry';
import ConnectionBanner from './components/shared/ConnectionBanner';

installConnectionMonitor();
initClientTelemetry();
import { useVersionCheck } from './hooks/useVersionCheck';
import AnnouncementPopup from './components/shared/AnnouncementPopup';

// Contexts
import { AvatarCacheProvider, useAvatarCache } from './contexts';
import { useDesign } from './contexts/DesignContext';
import { isStudioDesign } from './lib/design';
import { useNotifications } from './hooks/useNotifications';

/**
 * D22 (121 §5) · how many items the dashboard's Team Comms widget keeps.
 *
 * The Studio widget is taller than classic's, so the owner asked it to show the
 * 5 most recent items; classic keeps its cap of 2. The cap is applied to the
 * already-fetched list at every point that writes `globalRecentComms`, so both
 * designs issue exactly the same requests with exactly the same shapes — the
 * only difference is how many of the rows that came back survive the slice.
 * A function, not a constant: the design toggle flips the attribute at runtime.
 */
const recentCommsCap = () => (isStudioDesign() ? 5 : 2);

// ==================== EAGER LOADED (needed immediately) ====================
import { AuthPage, ResetPasswordPage, ChurchSignupPage, SignupSuccessPage } from './components/auth';
import { Dashboard } from './components/dashboard';
import TeamMemberView from './components/team/TeamMemberView';
import ImportantPostView from './components/team/ImportantPostView';
import FloatingThemeToggle from './components/shared/FloatingThemeToggle';
import DownloadAppContent from './components/shared/DownloadAppContent';
import DownloadAppGate from './components/shared/DownloadAppGate';
// QuickComposeModal lives at App-level so the global Shift+M shortcut can open
// it from any route. Already bundled with Dashboard widgets — no extra cost.
import QuickComposeModal from './components/messaging/QuickComposeModal';
// ServiceDetailFloater renders inside QuickComposeModal's right sidebar slot
// when an admin opens Quick Message from the Planning Center widget.
import ServiceDetailFloater from './components/dashboard/ServiceDetailFloater';

// ==================== LAZY LOADED (loaded on demand) ====================
// These components are loaded only when the user navigates to them
// We store import functions separately so we can prefetch them

// Import functions (stored for prefetching)
const importPlanner = () => import('./components/planner');
const importWorshipAIChat = () => import('./components/ai/WorshipAIChat');
const importSettings = () => import('./components/settings');
const importComms = () => import('./components/comms');
const importOnboardingApp = () => import('./components/onboarding/OnboardingApp');
const importPublicIntakeForm = () => import('./components/onboarding/PublicIntakeForm');
const importPublicTrainingPage = () => import('./components/onboarding/PublicTrainingPage');
const importPublicAuditionPrepPage = () => import('./components/onboarding/PublicAuditionPrepPage');
const importMessagingApp = () => import('./components/messaging');
const importLandingPage = () => import('./pages/LandingPage');
const importServices = () => import('./components/services');

// Lazy components using the stored import functions
const PlannerApp = lazy(() => importPlanner().then(m => ({ default: m.PlannerApp })));
const WorshipAIChat = lazy(() => importWorshipAIChat());
const ProfilePage = lazy(() => importSettings().then(m => ({ default: m.ProfilePage })));
const BillingPage = lazy(() => importSettings().then(m => ({ default: m.BillingPage })));
const BillingSuccessPage = lazy(() => importSettings().then(m => ({ default: m.BillingSuccessPage })));
const BlockedUserPage = lazy(() => importSettings().then(m => ({ default: m.BlockedUserPage })));
const SubscriptionPausedPage = lazy(() => importSettings().then(m => ({ default: m.SubscriptionPausedPage })));
const CalendarSyncPage = lazy(() => importSettings().then(m => ({ default: m.CalendarSyncPage })));
const TeamManagementPage = lazy(() => importSettings().then(m => ({ default: m.TeamManagementPage })));
const NotificationSettings = lazy(() => importSettings().then(m => ({ default: m.NotificationSettings })));
const AppStatsDashboard = lazy(() => importSettings().then(m => ({ default: m.AppStatsDashboard })));
const PlanningCenterSettingsPage = lazy(() => importSettings().then(m => ({ default: m.PlanningCenterSettingsPage })));
const TeamCommsApp = lazy(() => importComms().then(m => ({ default: m.TeamCommsApp })));
const OnboardingApp = lazy(() => importOnboardingApp());
const PublicIntakeForm = lazy(() => importPublicIntakeForm());
const PublicTrainingPage = lazy(() => importPublicTrainingPage());
const PublicAuditionPrepPage = lazy(() => importPublicAuditionPrepPage());
const TrainingLibraryHome = lazy(() => import('./components/training/TrainingLibraryHome'));
const TutorialVideosPage = lazy(() => import('./components/help/TutorialVideosPage'));
const JoinByLinkPage = lazy(() => import('./components/auth/JoinByLinkPage'));
const TrainingInstrumentPage = lazy(() => import('./components/training/TrainingInstrumentPage'));
const TrainingVideoPage = lazy(() => import('./components/training/TrainingVideoPage'));
const FeaturedWorkshopPage = lazy(() => import('./components/training/FeaturedWorkshopPage'));
const TeamDocsPage = lazy(() => import('./components/training/TeamDocsPage'));
const MessagingApp = lazy(() => importMessagingApp().then(m => ({ default: m.MessagingApp })));
const LandingPage = lazy(() => importLandingPage());
const InviteAcceptHandler = lazy(() => import('./components/auth/InviteAcceptHandler'));
const ServicesApp = lazy(() => importServices().then(m => ({ default: m.ServicesApp })));
const PublicRespondPage = lazy(() => import('./components/services/PublicRespondPage'));

// ==================== PREFETCH COMPONENTS ====================
// Downloads JS chunks in the background without mounting components.
// This avoids the ~30 wasted API calls that PreloadComponents triggered
// while still ensuring fast navigation (chunks are in browser cache).
const PrefetchComponents = React.memo(({ user }) => {
  useEffect(() => {
    if (!user) return;
    // Prefetch main app chunks after initial render settles
    const timer = setTimeout(() => {
      importComms();
      importPlanner();
      importServices();
      importOnboardingApp();
      importWorshipAIChat();
    }, 2000);
    // Prefetch settings chunks slightly later
    const timer2 = setTimeout(() => {
      importSettings();
      importMessagingApp();
      // Training library + workshop pages (dashboard widget links here)
      import('./components/training/TrainingLibraryHome');
      import('./components/training/FeaturedWorkshopPage');
      import('./components/training/TrainingInstrumentPage');
    }, 4000);
    return () => { clearTimeout(timer); clearTimeout(timer2); };
  }, [user]);
  return null;
});

// ==================== KEEP-ALIVE APP CONTAINER ====================
// Keeps apps mounted but hidden when not active, so 2nd visit is instant
const KeepAliveApps = React.memo(({ 
  user, 
  activeApp, 
  onboardingProps,
  worshipAIProps 
}) => {
  // Track which apps have been visited (keep mounted after first visit)
  const [visitedApps, setVisitedApps] = useState({});
  
  useEffect(() => {
    if (activeApp && !visitedApps[activeApp]) {
      setVisitedApps(prev => ({ ...prev, [activeApp]: true }));
    }
  }, [activeApp, visitedApps]);
  
  // Mount if: currently active OR was previously visited
  const shouldMountOnboarding = activeApp === 'onboarding' || visitedApps.onboarding;
  const shouldMountAIChat = activeApp === 'aichat' || visitedApps.aichat;
  
  return (
    <>
      {/* Onboarding - mount immediately when active, keep mounted after */}
      {shouldMountOnboarding && (
        <div style={{ display: activeApp === 'onboarding' ? 'block' : 'none' }}>
          <Suspense fallback={<LoadingSpinner />}>
            <OnboardingApp {...onboardingProps} />
          </Suspense>
        </div>
      )}
      
      {/* Worship AI - mount immediately when active, keep mounted after */}
      {shouldMountAIChat && (
        <div style={{ display: activeApp === 'aichat' ? 'block' : 'none' }}>
          <Suspense fallback={<LoadingSpinner />}>
            <WorshipAIChat {...worshipAIProps} />
          </Suspense>
        </div>
      )}
    </>
  );
});

// Loading spinner component for Suspense fallback
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <Loader2 className="w-8 h-8 animate-spin text-indigo-600 dark:text-indigo-400" />
  </div>
);

// Firestore for fetching recent messages
import { getFirestoreDb } from './lib/firebase';
import { collection, query, where, orderBy, limit, getDocs, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { COLLECTIONS } from './lib/firestore/schema';
import { subscribeToArchivedConversations } from './lib/firestore';

// WebSocket hook for real-time updates
import useWebSocket from './hooks/useWebSocket';

// Centralized HTML utilities
import { stripHtml } from './utils/html';
// Services (Simple Planner), Fix 7 G4: who lands where on a Services notification tap
import { servicesAdminFor } from './components/services/hooks/useServicesMode';

// Helper to get initial app from URL (for OAuth callbacks)
const getInitialAppFromURL = () => {
  const path = window.location.pathname;
  if (path === '/planner' || path === '/planner/') return 'planner';
  if (path === '/onboarding' || path === '/onboarding/') return 'onboarding';
  if (path === '/tutorials' || path === '/tutorials/') return 'tutorials';
  if (path === '/worship-ai' || path === '/worship-ai/') return 'aichat';  // P2-g: refill success reload
  if (path === '/services' || path === '/services/') return 'services';
  if (path.startsWith('/settings')) return 'settings';
  return null;
};

// Helper to get initial settings page from URL
const getInitialSettingsFromURL = () => {
  const path = window.location.pathname;
  if (path === '/settings/calendar' || path === '/settings/calendar/') return 'calendar';
  if (path === '/settings/profile' || path === '/settings/profile/') return 'profile';
  if (path === '/settings/billing' || path === '/settings/billing/') return 'billing';
  if (path === '/settings/team' || path === '/settings/team/') return 'team';
  if (path === '/settings/app-stats' || path === '/settings/app-stats/') return 'app-stats';
  if (path === '/settings/planning-center' || path === '/settings/planning-center/') return 'planning-center';
  return null;
};

// Capture deep-link query params at app boot. The auth flow can rewrite
// window.location.search before settings pages mount, so we snapshot it
// once on initial module evaluation and stash on window.__bootQuery.
// Components later read from window.__bootQuery instead of live URL.
(() => {
  if (typeof window === 'undefined') return;
  if (window.__bootQuery) return;
  try {
    const p = new URLSearchParams(window.location.search);
    window.__bootQuery = Object.fromEntries(p.entries());
  } catch (e) { window.__bootQuery = {}; }
})();

function App() {
  useVersionCheck();
  const { preloadAvatars: seedAvatarCache, getCachedAvatar } = useAvatarCache();
  const { syncFromServer: syncDesignFromServer } = useDesign();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentApp, setCurrentApp] = useState(getInitialAppFromURL());
  const [servicesInitial, setServicesInitial] = useState(null);
  // Re-render the (window.location-based) router on client-side navigation.
  // `spaNavigate` pushes history + dispatches popstate; this bump makes the
  // render re-read window.location so in-app transitions skip a full reload.
  const [, setRouteTick] = useState(0);
  const [plannerView, setPlannerView] = useState('month');
  const [isResetPassword, setIsResetPassword] = useState(false);
  // Friendly message shown on the login screen when a recovery link is dead
  // (expired, already used, or — most commonly — consumed by an email security
  // scanner that prefetched the one-time link before the user clicked it).
  const [recoveryError, setRecoveryError] = useState('');
  const [initialAIMessage, setInitialAIMessage] = useState('');
  const [autoSubmitAI, setAutoSubmitAI] = useState(false);
  // D18 (121 §5): the conversation a Studio dashboard "Recent" row asked the AI app
  // to open. Null on every classic path — nothing classic passes an options object.
  const [initialAIConversationId, setInitialAIConversationId] = useState(null);
  const [currentSettings, setCurrentSettings] = useState(getInitialSettingsFromURL());
  const [userPlan, setUserPlan] = useState('free');
  const [commsInitialTab, setCommsInitialTab] = useState(null);
  const [commsServiceHighlight, setCommsServiceHighlight] = useState(null);   // Fix 7 G4: {serviceId, type}
  const [commsHighlightPostId, setCommsHighlightPostId] = useState(null);
  const [commsHighlightCommentId, setCommsHighlightCommentId] = useState(null);
  const [commsHighlightMessageId, setCommsHighlightMessageId] = useState(null);
  const [commsInitialConversationId, setCommsInitialConversationId] = useState(null);
  const [onboardingHighlightCandidateId, setOnboardingHighlightCandidateId] = useState(() => {
    // Parse candidate ID from URL for deep linking from email notifications
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('candidate') || null;
  });
  
  // Subscription/billing state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [churchGone, setChurchGone] = useState(false);
  
  // Planning Center post-connection prompt
  const [showPCConnectedPrompt, setShowPCConnectedPrompt] = useState(false);

  // Quick Compose modal — lifted to App level so the Shift+M global shortcut
  // can open it from any authenticated route. Dashboard's existing Compose
  // button calls the same setter via prop. PCO Planning Center widget also
  // funnels into these setters via the onQuickMessage callback.
  const [showQuickCompose, setShowQuickCompose] = useState(false);
  // `pcoServicePlan` is an atomic snapshot of the Planning Center plan that
  // was active when the user clicked the PC widget's Quick Message button.
  // Holds: { team_members, group_name, plan_url, songs, song_count, date, time }.
  // Null for the regular (non-PCO) Quick Message flow.
  const [pcoServicePlan, setPcoServicePlan] = useState(null);
  
  

  // GLOBAL DATA CACHE - shared across Dashboard, Planner, and Team Comms
  // Initialize from localStorage cache for instant first render
  const [globalTasks, setGlobalTasks] = useState(() => {
    try {
      const cached = localStorage.getItem('cache_tasks');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });
  // R28: `cache_priorities` / `cache_goals` were read here but never written
  // anywhere — priorities and goals load from Supabase.
  const [globalPriorities, setGlobalPriorities] = useState([]);
  const [globalGoals, setGlobalGoals] = useState([]);
  const [globalPosts, setGlobalPosts] = useState(() => {
    try {
      const cached = localStorage.getItem('cache_posts');
      const cachedChurchId = localStorage.getItem('cache_church_id');
      // Only use cache if it belongs to the same church (prevent cross-church leaks)
      if (cached && cachedChurchId) return JSON.parse(cached);
      return [];
    } catch { return []; }
  });
  const [globalConversations, setGlobalConversations] = useState([]);
  const [globalRecentComms, setGlobalRecentComms] = useState(() => {
    try {
      const cached = localStorage.getItem('cache_recentComms');
      if (cached) {
        const parsed = JSON.parse(cached);
        // Validate cache has author.id for avatar caching - if not, discard stale cache
        const hasValidAuthorIds = parsed.every(item => item.author?.id || item.user_id);
        if (!hasValidAuthorIds) {
          localStorage.removeItem('cache_recentComms');
          return [];
        }
        // Strip HTML from cached posts and cap (D22: 5 in Studio, 2 in classic)
        const processed = parsed.map(item => ({
          ...item,
          content: item.type === 'post' ? stripHtml(item.content) : item.content
        })).slice(0, recentCommsCap());
        return processed;
      }
      return [];
    } catch { return []; }
  });
  const [dataLoaded, setDataLoaded] = useState(() => {
    // If we have cached data, consider it "loaded" for instant render
    return !!localStorage.getItem('cache_tasks');
  });
  
  // GLOBAL MESSAGE NOTIFICATIONS — single source of truth via useNotifications hook
  const notificationHook = useNotifications(user?.id);
  // Services (Simple Planner): a service notification arriving refreshes the
  // open Services mini-app (L6-2; the sharedNotifStamp pattern in TeamCommsApp).
  const servicesNotifStamp = React.useMemo(() => {
    const list = (notificationHook?.notifications || []).filter(n => ['service_request','service_response','service_updated','service_comment'].includes(n.type));
    return list.length ? `${list.length}:${list[0]?.id || ''}` : '';
  }, [notificationHook?.notifications]);
  // Fix 7 G4: the service-worker click handler is registered once, so it reads
  // the signed-in user through this ref.
  const svcUserRef = useRef(user);
  svcUserRef.current = user;
  const pendingSvcTapRef = useRef(null);   // Fix 7c F16: a tap that arrived before the user was known
  
  // ARCHIVED CONVERSATIONS - for filtering Team Comms widget on dashboard
  const [archivedConversationIds, setArchivedConversationIds] = useState([]);
  
  // FIREBASE AUTH READY - gates Firestore listeners to prevent INTERNAL ASSERTION FAILED errors
  // on first login when onSnapshot is called before Firebase Auth completes
  const [firebaseReady, setFirebaseReady] = useState(false);
  
  // GLOBAL ONBOARDING DATA - prefetched for instant load
  const [globalOnboardingData, setGlobalOnboardingData] = useState(null);
  
  // PENDING ONBOARDING NOTIFICATIONS - accumulates WS notifications while Dashboard is unmounted
  // When Dashboard mounts, it drains this ref and merges into its local state
  const pendingOnboardingNotifsRef = useRef([]);
  
  // Quick Add Todo state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddText, setQuickAddText] = useState('');
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [quickAddSelectedIndex, setQuickAddSelectedIndex] = useState(0);
  
  // Global Hover Delete state (shared across Dashboard, Planner, Team Comms, etc.)
  const [hoveredItem, setHoveredItem] = useState(null); // { type: 'task'|'priority'|'goal'|'weekly_priority'|'shared_task'|'shared_priority'|'shared_goal'|'post', id: string, data: object, deleteFn?: () => void|Promise }
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Pre-fetch ALL data using Supabase (same source as components)
  const prefetchAllData = useCallback(async (userId, churchId = null) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    // Invalidate stale cache if church changed (prevents cross-church data leaks)
    const cachedChurchId = localStorage.getItem('cache_church_id');
    if (churchId && cachedChurchId && cachedChurchId !== churchId) {
      localStorage.removeItem('cache_posts');
      localStorage.removeItem('cache_recentComms');
      setGlobalPosts([]);
      setGlobalRecentComms([]);
    }
    
    try {
      // Fetch TASKS only here. Tasks are date-INDEPENDENT (the user's full set),
      // so refreshing them on login / focus / TOKEN_REFRESHED is safe and useful.
      //
      // Month-scoped `priorities` and year-scoped `annual_goals` are deliberately
      // NOT fetched here. prefetchAllData re-runs on tab focus (visibilitychange)
      // and on every Supabase TOKEN_REFRESHED, and it only knows "today" — so
      // fetching those clobbered the Planner's SELECTED month/year whenever the tab
      // regained focus (e.g. viewing July, tab away 5s and back → the priorities
      // list silently emptied / swapped to the current month). PlannerApp is the
      // single owner and fetches them for the selected month/year via its own
      // keyed effects. Do not re-add them here.
      const tasksResult = await fetchAllTasks(supabase, userId);

      if (tasksResult.data) {
        setGlobalTasks(tasksResult.data);
        localStorage.setItem('cache_tasks', JSON.stringify(tasksResult.data));
      }
      
      // Mark data as loaded so dashboard renders NOW (don't wait for posts)
      setDataLoaded(true);
      
      // Fetch posts and messages in background for dashboard widget
      if (churchId) {
        // Seed avatar cache from MongoDB (single source of truth for all avatars)
        fetch(`${API_URL}/api/user/team/${encodeURIComponent(churchId)}`)
          .then(res => res.ok ? res.json() : { members: [] })
          .then(data => {
            if (data.members) {
              seedAvatarCache(data.members);
            }
          })
          .catch(err => console.error('Error seeding avatar cache:', err));

        // Fetch posts
        const postsPromise = fetch(`${API_URL}/api/comms/v3/posts?church_id=${encodeURIComponent(churchId)}&limit=20&viewer_id=${encodeURIComponent(userId)}`)

          .then(res => res.ok ? res.json() : { posts: [] })
          .then(postsJson => {
            const fetchedPosts = postsJson.posts || [];
            setGlobalPosts(fetchedPosts);
            localStorage.setItem('cache_posts', JSON.stringify(fetchedPosts));
            localStorage.setItem('cache_church_id', churchId);
            return fetchedPosts;
          })
          .catch(err => {
            console.error('Error fetching posts:', err);
            return [];
          });
        
        // Fetch recent messages from Firestore (optimized for widget - only need 2 items max)
        const messagesPromise = (async () => {
          try {
            const db = getFirestoreDb();
            
            // Get only 5 most recent conversations (we only show 2 items in widget)
            const convsRef = collection(db, COLLECTIONS.CONVERSATIONS);
            const convsQuery = query(
              convsRef,
              where('participants', 'array-contains', userId),
              orderBy('updatedAt', 'desc'),
              limit(5)
            );
            const convsSnapshot = await getDocs(convsQuery);
            
            const conversations = convsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // Fetch messages from all conversations in parallel (much faster than sequential)
            const messagePromises = conversations.map(async (conv) => {
              const messagesRef = collection(db, COLLECTIONS.CONVERSATIONS, conv.id, COLLECTIONS.MESSAGES);
              const msgQuery = query(messagesRef, orderBy('createdAt', 'desc'), limit(3));
              const msgSnapshot = await getDocs(msgQuery);
              
              // Find the most recent message NOT from the current user
              for (const msgDoc of msgSnapshot.docs) {
                const msg = { id: msgDoc.id, ...msgDoc.data() };
                
                if (msg.senderId !== userId) {
                  const senderDetails = conv.participantDetails?.[msg.senderId];
                  const senderName = senderDetails?.name || msg.senderName || 'Unknown';
                  const senderAvatar = getCachedAvatar(msg.senderId) || senderDetails?.avatar || msg.senderAvatar;
                  
                  return {
                    id: msg.id,
                    conversationId: conv.id,
                    type: 'message',
                    content: msg.text || (msg.images?.length ? '📷 Image' : msg.voiceMemo ? '🎤 Voice memo' : ''),
                    author: { id: msg.senderId, name: senderName, avatar: senderAvatar },
                    created_at: msg.createdAt?.toDate?.() || new Date(msg.createdAt),
                    sortDate: msg.createdAt?.toDate?.() || new Date(msg.createdAt),
                    conversationName: conv.type === 'group' ? conv.name : senderName
                  };
                }
              }
              return null;
            });
            
            const results = await Promise.all(messagePromises);
            return results.filter(Boolean);
          } catch (err) {
            console.error('Error fetching messages for widget:', err);
            return [];
          }
        })();
        
        // Combine posts and messages, sort by date, take top 2
        Promise.all([postsPromise, messagesPromise]).then(([posts, messages]) => {
          // Transform posts and strip HTML from content
          const recentPosts = posts
            .filter(post => post.user_id !== userId)
            .slice(0, 5)
            .map(post => ({
              ...post,
              type: 'post',
              content: stripHtml(post.content), // Strip HTML tags
              sortDate: new Date(post.created_at)
            }));
          
          // Combine and sort
          const allComms = [...recentPosts, ...messages];
          allComms.sort((a, b) => b.sortDate - a.sortDate);

          // Take the most recent items (D22: 5 in Studio, 2 in classic)
          const recentComms = allComms.slice(0, recentCommsCap());
          setGlobalRecentComms(recentComms);
          localStorage.setItem('cache_recentComms', JSON.stringify(recentComms));
        });
      }
      
      // Notifications are handled by the useNotifications hook (auto-fetches on userId change)
      
      // Prefetch onboarding data for instant load
      if (churchId) {
        setOnboardingActor(userId);
        const actorInit = { headers: { 'X-User-Id': userId } };
        Promise.all([
          fetch(`${API_URL}/api/onboarding/candidates/${churchId}`, actorInit).then(r => r.ok ? r.json() : { candidates: {} }),
          fetch(`${API_URL}/api/onboarding/settings/${churchId}`, actorInit).then(r => r.ok ? r.json() : null)
        ]).then(([candidatesData, settingsData]) => {
          setGlobalOnboardingData({
            candidates: candidatesData.candidates || candidatesData || {},
            settings: settingsData
          });
        }).catch(err => console.error('Error prefetching onboarding:', err));
      }
      
    } catch (err) {
      console.error('Prefetch error:', err);
      setDataLoaded(true); // Still show dashboard even if fetch fails
    }
  }, []);

  // Lightweight refetch of TASKS ONLY — called on tab focus, navigation, and
  // periodic polling. Tasks are date-INDEPENDENT here (we fetch the user's full
  // set), so refreshing them on focus/poll is always safe.
  //
  // IMPORTANT: do NOT refetch month-scoped `priorities` or year-scoped
  // `annual_goals` in this function. Those are owned exclusively by PlannerApp,
  // which fetches them for the user's SELECTED month/year via its own keyed
  // effects. This callback only knows "today", so fetching them here clobbers the
  // planner's selected-month data on every focus/poll/nav (e.g. viewing June, tab
  // away 5s and back → the list silently swapped to May's priorities under a June
  // header). Single-owner = PlannerApp. (Regression covered in tests.)
  const refetchTasks = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await fetchAllTasks(supabase, user.id);
      if (!error && data) {
        setGlobalTasks(data);
        localStorage.setItem('cache_tasks', JSON.stringify(data));
      }
    } catch (err) {
      console.error('Planner tasks refetch error:', err);
    }
  }, [user?.id, setGlobalTasks]);

  // Refetch on tab focus + poll every 60s while tab is visible
  // Client-side router re-render on Back/Forward + spaNavigate() pushes.
  useEffect(() => {
    const onPop = () => {
      // Sync app-level state with the URL so cross-app client-side
      // navigations (e.g. training library → /onboarding customize) and
      // browser Back/Forward land in the right app without a full reload.
      const app = getInitialAppFromURL();
      if (app || window.location.pathname === '/') {
        setCurrentApp(app);
        if (app === 'settings') setCurrentSettings(getInitialSettingsFromURL());
      }
      setRouteTick((t) => t + 1);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // macOS desktop app (WKWebView) maps Backspace outside a text field to
  // history back-navigation — landing users on a stale history entry (e.g. the
  // Training Library, the only area that pushes history entries). Modern web
  // browsers removed this behavior years ago, so blocking the default here is
  // a no-op on web. Capture phase + preventDefault only (no stopPropagation),
  // so typing and the hover-delete shortcut keep working untouched.
  useEffect(() => {
    const onBackspaceCapture = (e) => {
      if (e.key !== 'Backspace') return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onBackspaceCapture, true);
    return () => window.removeEventListener('keydown', onBackspaceCapture, true);
  }, []);


  useEffect(() => {
    let pollInterval = null;
    const startPolling = () => {
      if (!pollInterval) pollInterval = setInterval(refetchTasks, 60000);
    };
    const stopPolling = () => {
      if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetchTasks();
        startPolling();
      } else {
        stopPolling();
      }
    };
    if (document.visibilityState === 'visible') startPolling();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => { document.removeEventListener('visibilitychange', onVisibilityChange); stopPolling(); };
  }, [refetchTasks]);

  // Refetch tasks when navigating to Dashboard or Planner
  useEffect(() => {
    if (currentApp === null || currentApp === 'planner') refetchTasks();
  }, [currentApp, refetchTasks]);

  // ── Global Shift+M shortcut: open Quick Message from anywhere in the app ──
  // Guards:
  //   • Only fires for authenticated users
  //   • Skips when target is an input, textarea, or contenteditable element
  //     (prevents collision with typing in TipTap editors, search bars, DMs)
  //   • Requires EXACTLY Shift (no Ctrl/Cmd/Alt) to avoid Cmd-Shift-M / etc.
  //   • Skips key-repeat from holding the key down
  //   • Skips if the modal is already open
  useEffect(() => {
    if (!user) return;
    const onKeyDown = (e) => {
      if (e.repeat) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (!e.shiftKey || e.key !== 'M') return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (showQuickCompose) return;
      e.preventDefault();
      setShowQuickCompose(true);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [user, showQuickCompose]);

  // Real-time Firestore listener for DMs on Dashboard widget
  useEffect(() => {
    if (!user?.id || !firebaseReady || !isFirebaseSignedIn()) return;
    
    const db = getFirestoreDb();
    if (!db) return;
    
    // Listen to conversations where user is a participant (ordered by most recent)
    const convsRef = collection(db, COLLECTIONS.CONVERSATIONS);
    const convsQuery = query(
      convsRef,
      where('participants', 'array-contains', user.id),
      orderBy('updatedAt', 'desc'),
      limit(5)
    );
    
    const unsubscribe = onSnapshot(convsQuery, async (snapshot) => {
      try {
        const conversations = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Get recent messages from each conversation
        const recentMessages = [];
        // Cache for user profiles to avoid redundant lookups
        const userProfileCache = {};
        
        for (const conv of conversations) {
          const messagesRef = collection(db, COLLECTIONS.CONVERSATIONS, conv.id, COLLECTIONS.MESSAGES);
          const msgQuery = query(messagesRef, orderBy('createdAt', 'desc'), limit(5));
          const msgSnapshot = await getDocs(msgQuery);
          
          // Find the most recent message NOT from the current user
          for (const msgDoc of msgSnapshot.docs) {
            const msg = { id: msgDoc.id, ...msgDoc.data() };
            
            if (msg.senderId !== user.id) {
              const senderDetails = conv.participantDetails?.[msg.senderId];
              
              // Helper to check if a string looks like an email
              const looksLikeEmail = (str) => str && str.includes('@') && str.includes('.');
              
              // Get the best available name - prefer non-email names
              let senderName = 'Unknown';
              const candidateNames = [
                senderDetails?.name,
                msg.senderName
              ].filter(Boolean);
              
              // First try to find a name that's NOT an email
              const nonEmailName = candidateNames.find(name => !looksLikeEmail(name));
              if (nonEmailName) {
                senderName = nonEmailName;
              } else {
                // All available names look like emails - try to fetch from Firestore users collection
                try {
                  if (!userProfileCache[msg.senderId]) {
                    const userDocRef = doc(db, COLLECTIONS.USERS, msg.senderId);
                    const userDocSnap = await getDoc(userDocRef);
                    if (userDocSnap.exists()) {
                      userProfileCache[msg.senderId] = userDocSnap.data();
                    }
                  }
                  const cachedProfile = userProfileCache[msg.senderId];
                  if (cachedProfile?.name && !looksLikeEmail(cachedProfile.name)) {
                    senderName = cachedProfile.name;
                  } else if (candidateNames.length > 0) {
                    senderName = candidateNames[0];
                  }
                } catch (err) {
                  console.error('Error fetching user profile:', err);
                  if (candidateNames.length > 0) {
                    senderName = candidateNames[0];
                  }
                }
              }
              
              const senderAvatar = getCachedAvatar(msg.senderId) || senderDetails?.avatar || msg.senderAvatar || userProfileCache[msg.senderId]?.avatar;
              
              recentMessages.push({
                id: msg.id,
                conversationId: conv.id,
                type: 'message',
                content: msg.text || (msg.images?.length ? '📷 Image' : msg.voiceMemo ? '🎤 Voice memo' : ''),
                author: { name: senderName, avatar: senderAvatar, id: msg.senderId },
                created_at: msg.createdAt?.toDate?.() || new Date(msg.createdAt),
                sortDate: msg.createdAt?.toDate?.() || new Date(msg.createdAt),
                conversationName: conv.type === 'group' ? conv.name : senderName
              });
              break;
            }
          }
        }
        
        // Update globalRecentComms by merging DMs with posts
        setGlobalRecentComms(prev => {
          // Keep existing posts (already have HTML stripped), replace DMs
          const existingPosts = prev.filter(item => item.type === 'post');
          const allComms = [...existingPosts, ...recentMessages];
          allComms.sort((a, b) => {
            const dateA = a.sortDate || new Date(a.created_at);
            const dateB = b.sortDate || new Date(b.created_at);
            return dateB - dateA;
          });
          // Hard cap (D22: 5 in Studio, 2 in classic)
          return allComms.slice(0, recentCommsCap());
        });
        
      } catch (err) {
        console.error('Error in Firestore listener:', err);
      }
    }, (error) => {
      console.error('Firestore conversation listener error:', error);
    });
    
    return () => unsubscribe();
  }, [user?.id, firebaseReady]);

  // Subscribe to archived conversations for filtering Team Comms widget
  useEffect(() => {
    if (!user?.id || !firebaseReady || !isFirebaseSignedIn()) {
      setArchivedConversationIds([]);
      return;
    }

    const unsubscribe = subscribeToArchivedConversations(user.id, (archivedIds) => {
      setArchivedConversationIds(archivedIds);
    });

    return () => unsubscribe();
  }, [user?.id, firebaseReady]);

  // GLOBAL NOTIFICATION FUNCTIONS — all handled by useNotifications hook

  // Mark messages as read in Firestore and update conversation unread counts
  // P2-h (DIV-032): the old implementation POSTed to /comms/v3/messages/read,
  // a route that does not exist. Read state for DMs lives in Firestore and is
  // written by the messaging components themselves; keep the prop contract as
  // a no-op so child components need no change.
  const markMessagesAsRead = useCallback(async () => {}, []);

  // WebSocket message handler for real-time updates
  const handleWebSocketMessage = useCallback((data) => {
    console.log('[WebSocket] Received:', data.type);
    
    switch (data.type) {
      case 'new_post':
        if (!data.post) {
          console.warn('[WebSocket] new_post missing post data, skipping');
          break;
        }
        // Add new post to the feed instantly
        console.log('[WebSocket] new_post data:', data.post);
        console.log('[WebSocket] new_post id:', data.post?.id);
        setGlobalPosts(prev => {
          console.log('[WebSocket] Current posts count:', prev.length);
          // Check if post already exists by real ID
          if (prev.some(p => p.id === data.post.id)) {
            console.log('[WebSocket] Post already exists, skipping');
            return prev;
          }
          // If this is the current user's own post, check for an optimistic version
          // (race condition: WS can arrive before HTTP response replaces temp-ID)
          if (data.post.user_id === user?.id) {
            const optimisticIndex = prev.findIndex(p => p._isOptimistic && p.user_id === user?.id);
            if (optimisticIndex !== -1) {
              console.log('[WebSocket] Replacing optimistic post with real post');
              const updated = [...prev];
              updated[optimisticIndex] = { ...data.post, _isOptimistic: false };
              return updated;
            }
          }
          console.log('[WebSocket] Adding new post to feed');
          return [data.post, ...prev];
        });
        // Update recent comms - strip HTML and cap at 2
        // Skip adding to recent comms if this is the current user's own post
        if (data.post.user_id !== user?.id) {
          setGlobalRecentComms(prev => {
            const newItem = {
              ...data.post,
              type: 'post',
              content: stripHtml(data.post.content),
              sortDate: new Date(data.post.created_at)
            };
            const updated = [newItem, ...prev.filter(item => item.id !== data.post.id)];
            updated.sort((a, b) => {
              const dateA = a.sortDate || new Date(a.created_at);
              const dateB = b.sortDate || new Date(b.created_at);
              return dateB - dateA;
            });
            return updated.slice(0, recentCommsCap()); // Hard cap (D22: 5 Studio / 2 classic)
          });
        }
        break;
        
      // R28 (DIV-049): the server never emits `new_message` (DMs are Firestore
      // listeners), `typing`, `typing_start/stop`, `message_read` or `poll_vote`;
      // those handlers were dead and are removed.
        
      case 'new_comment':
        // Update post comment_count (PostCard manages its own local comments array)
        setGlobalPosts(prev => {
          return prev.map(post => {
            if (post.id === data.post_id) {
              // Prevent duplicate count increment by tracking last processed comment
              const lastCommentId = post._lastWsCommentId;
              if (lastCommentId === data.comment?.id) return post;
              return {
                ...post,
                _lastWsCommentId: data.comment?.id,
                comment_count: (post.comment_count || 0) + 1
              };
            }
            return post;
          });
        });
        // Dispatch event for PostCard components to update their local comments state
        window.dispatchEvent(new CustomEvent('ws_new_comment', { detail: data }));
        break;
        
      case 'notification':
        // Real-time notification — add to hook state (with "born as read" for active conversation)
        console.log('[WebSocket] notification received:', data.notification);
        notificationHook.addNotification(data.notification);
        break;
        
      case 'notification_read':
        // Cross-device read sync — another tab/device marked notifications as read
        notificationHook.syncReadFromRemote(data);
        break;
        
      case 'onboarding_notification':
        // Real-time onboarding notification — accumulate in ref AND dispatch event
        // The ref ensures notifications survive even if Dashboard is unmounted.
        // The window event allows Dashboard to pick them up instantly when mounted.
        console.log('[WebSocket] onboarding notification received:', data.notification);
        if (data.notification) {
          pendingOnboardingNotifsRef.current = [
            ...pendingOnboardingNotifsRef.current.filter(n => n.id !== data.notification.id),
            data.notification
          ];
        }
        window.dispatchEvent(new CustomEvent('ws_onboarding_notification', { detail: data.notification }));
        break;
      
      case 'typing_indicator':
        // Dispatch typing indicator for MessagingApp (DM/group chats)
        window.dispatchEvent(new CustomEvent('ws_typing_indicator', { detail: data }));
        break;
      
      case 'presence':
        // User came online/offline
        console.log(`[WebSocket] User ${data.user_id} is now ${data.status}`);
        break;
        
      case 'reaction_update':
        // Update reactions on a post (no notification)
        setGlobalPosts(prev => {
          return prev.map(post => {
            if (post.id === data.post_id) {
              const reactions = { ...post.reactions };
              if (data.action === 'added') {
                if (!reactions[data.emoji]) {
                  reactions[data.emoji] = [];
                }
                if (!reactions[data.emoji].includes(data.user_id)) {
                  reactions[data.emoji].push(data.user_id);
                }
              } else if (data.action === 'removed') {
                if (reactions[data.emoji]) {
                  reactions[data.emoji] = reactions[data.emoji].filter(id => id !== data.user_id);
                  if (reactions[data.emoji].length === 0) {
                    delete reactions[data.emoji];
                  }
                }
              }
              // Merge the reactor's name so the who-reacted tooltip resolves it
              // immediately instead of falling back to "User" until a refetch.
              const reaction_names = { ...(post.reaction_names || {}) };
              if (data.reactor_name && data.user_id) {
                reaction_names[data.user_id] = data.reactor_name;
              }
              return { ...post, reactions, reaction_names };
            }
            return post;
          });
        });
        break;

      case 'post_updated':
        // Update post content/pinned status
        setGlobalPosts(prev => {
          return prev.map(post => {
            if (post.id === data.post_id) {
              return { ...post, ...data.updates };
            }
            return post;
          });
        });
        break;
        
      case 'post_deleted':
        // Remove deleted post
        setGlobalPosts(prev => prev.filter(post => post.id !== data.post_id));
        break;
        
      case 'comment_deleted':
        // Remove deleted comment and its replies from post
        setGlobalPosts(prev => {
          return prev.map(post => {
            if (post.id === data.post_id) {
              const remaining = (post.comments || []).filter(c => c.id !== data.comment_id && c.parent_comment_id !== data.comment_id);
              const removedCount = (post.comments || []).length - remaining.length;
              return {
                ...post,
                comments: remaining,
                comment_count: Math.max(0, (post.comment_count || 0) - removedCount)
              };
            }
            return post;
          });
        });
        break;

      case 'comment_reaction_update':
        // Dispatch custom event for PostCard to handle comment reaction updates
        window.dispatchEvent(new CustomEvent('ws_comment_reaction', { detail: data }));
        break;

      case 'poll_update':
        // Update poll results (backend sends poll_update)
        setGlobalPosts(prev => {
          return prev.map(post => {
            if (post.id === data.post_id) {
              return { ...post, poll: data.poll };
            }
            return post;
          });
        });
        break;
        
        
      case 'email_results':
        window.dispatchEvent(new CustomEvent('ws_email_results', { detail: data }));
        break;

      case 'planner_note_updated':
        // Daily Notes real-time sync — handled inside PlannerApp
        window.dispatchEvent(new CustomEvent('ws_planner_note', { detail: data }));
        break;

      case 'shared_item_sync':
        // Shared goals/priorities/tasks changed by someone else — instant refetch
        window.dispatchEvent(new CustomEvent('ws_shared_item_sync', { detail: data }));
        break;

      default:
        console.log('[WebSocket] Unhandled event type:', data.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Design language: localStorage paints first (public/index.html), then the
  // profile reconciles so the choice follows the user across devices.
  // docs/app-map/102 §1.
  useEffect(() => {
    if (user?.id) syncDesignFromServer(user.id);
  }, [user?.id, syncDesignFromServer]);

  // Initialize WebSocket connection
  const { isConnected, sendTyping } = useWebSocket(
    user?.id,  // V2: Always use Supabase UUID for WebSocket
    user?.church_id,
    handleWebSocketMessage
  );

  // Refetch notifications when WebSocket reconnects (catches missed notifications)
  const wasConnectedRef = React.useRef(false);
  useEffect(() => {
    if (isConnected && !wasConnectedRef.current && user?.id) {
      // WebSocket just reconnected — refetch to catch anything missed
      notificationHook.refetch();
    }
    wasConnectedRef.current = isConnected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, user?.id, notificationHook.refetch]);

  // Polling safety net: refetch notifications every 60s when WebSocket is disconnected
  useEffect(() => {
    if (!user?.id || isConnected) return;
    const interval = setInterval(() => {
      notificationHook.refetch();
    }, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isConnected, notificationHook.refetch]);

  // Register for FCM push notifications when user logs in
  useEffect(() => {
    if (!user?.id || !firebaseReady) return;
    
    // Request permission and register token (idempotent — safe to call repeatedly)
    const registerPush = async () => {
      if (!isPushSupported()) return;
      
      if (Notification.permission === 'granted') {
        // Already granted — just refresh the token (handles iOS token expiry)
        await refreshPushSubscription(user.id);
      } else if (Notification.permission === 'default') {
        // Not yet asked — request permission
        await requestNotificationPermission(user.id);
      }
      // 'denied' — do nothing, user blocked notifications
    };
    
    // Slight delay to not block initial render
    const timer = setTimeout(registerPush, 2000);
    return () => clearTimeout(timer);
  }, [user?.id, firebaseReady]);

  // Listen for FCM foreground messages — triggers notification refetch
  useEffect(() => {
    if (!user?.id || !isPushSupported()) return;
    
    let unsubscribe;
    try {
      unsubscribe = onForegroundMessage((payload) => {
        console.log('[FCM] Foreground message:', payload);
        // R17: silent sync pushes carry no new notification (WS already updates open
        // tabs) — but when this tab is visible the push is delivered HERE instead
        // of the service worker, so close matching OS banners from the page.
        if (payload?.data?.type === 'sync') {
          const d = payload.data || {};
          const matches = (n) => {
            const nd = (n && n.data) || {};
            if (d.scope === 'all') return true;
            if (d.scope === 'single') return !!d.notification_id && nd.notification_id === d.notification_id;
            if (d.scope === 'content') {
              return (!!d.post_id && nd.post_id === d.post_id)
                || (!!d.comment_id && nd.comment_id === d.comment_id)
                || (!!d.conversation_id && nd.conversation_id === d.conversation_id);
            }
            return false;
          };
          try {
            navigator.serviceWorker?.getRegistration('/firebase-messaging-sw.js')
              .then((reg) => (reg ? reg.getNotifications() : []))
              .then((list) => { (list || []).forEach((n) => { if (matches(n)) n.close(); }); })
              .catch(() => {});
          } catch (e) { /* no SW support */ }
          return;
        }
        // Refetch notifications to pick up the new one
        notificationHook.refetch();
      });
    } catch (err) {
      console.warn('[FCM] Could not set up foreground listener:', err);
    }
    
    return () => { if (unsubscribe) unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, notificationHook.refetch]);

  // Listen for send_typing events from MessagingApp and forward to WebSocket
  useEffect(() => {
    if (!sendTyping || !user) return;
    
    const handleSendTyping = (event) => {
      const { conversationId, participantIds, isTyping } = event.detail;
      const userName = user.name || user.email?.split('@')[0] || 'Someone';
      sendTyping(conversationId, participantIds, userName, isTyping);
    };
    
    window.addEventListener('send_typing', handleSendTyping);
    return () => window.removeEventListener('send_typing', handleSendTyping);
  }, [sendTyping, user]);

  useEffect(() => {
    // Initialize PostHog analytics
    initPostHog();
    
    // Flag to prevent race conditions between initSession and onAuthStateChange
    let isInitialized = false;
    let isMounted = true;
    
    // Cache enrichment data so Dashboard has church_created_at even if API fails on next load
    const cacheUserEnrichment = (userId, enrichment) => {
      try { localStorage.setItem(`user_enrichment_${userId}`, JSON.stringify(enrichment)); } catch { /* ignore */ }
    };
    const getCachedEnrichment = (userId) => {
      try { return JSON.parse(localStorage.getItem(`user_enrichment_${userId}`)); } catch { return null; }
    };
    const enrichFromCache = (sessionUser) => {
      const cached = getCachedEnrichment(sessionUser.id);
      if (!cached) return sessionUser;
      return { ...sessionUser, ...cached, comms_user_id: sessionUser.id };
    };
    
    // Check for existing session and enrich with church info
    const initSession = async () => {
      try {
        const { data } = await getSession();
        const sessionUser = data?.session?.user ?? null;
        
        if (sessionUser && isMounted) {
          isInitialized = true;
          // Fetch church info to enrich user object
          try {
            const API_URL = process.env.REACT_APP_BACKEND_URL;
            const userInfoResponse = await fetch(`${API_URL}/api/auth/user-info/${sessionUser.id}`);
            if (userInfoResponse.ok && isMounted) {
              const userInfo = await userInfoResponse.json();
              const enrichedUser = {
                ...sessionUser,
                church_id: userInfo.church_id,
                church_name: userInfo.church_name,
                church_slug: userInfo.church_slug,
                church_created_at: userInfo.church_created_at,
                role: userInfo.role,
                web_access: userInfo.web_access,
                is_super_admin: userInfo.is_super_admin === true,
                name: userInfo.full_name || sessionUser.user_metadata?.full_name || sessionUser.user_metadata?.name || null,
                avatar: userInfo.avatar_url || sessionUser.user_metadata?.avatar_url,
                comms_user_id: sessionUser.id  // V2: Always use Supabase UUID
              };
              setUser(enrichedUser);
              identifyUser(enrichedUser);
              cacheUserEnrichment(sessionUser.id, {
                church_id: userInfo.church_id, church_name: userInfo.church_name,
                church_slug: userInfo.church_slug, church_created_at: userInfo.church_created_at,
                role: userInfo.role, web_access: userInfo.web_access, is_super_admin: userInfo.is_super_admin === true, name: enrichedUser.name, avatar: enrichedUser.avatar
              });
              // Must complete before Firestore listeners are safe to attach
              signIntoFirebase(enrichedUser).then(() => {
                if (isMounted) setFirebaseReady(true);
              }).catch(err => {
                console.warn('[Firebase Auth] Initial sign-in failed:', err);
                if (isMounted) setFirebaseReady(true); // Still allow app to function
              });
              // Pre-fetch data for instant app loading
              prefetchAllData(sessionUser.id, userInfo.church_id);
            } else if (isMounted) {
              setUser(enrichFromCache(sessionUser));
              // Sign into Firebase with basic user info
              signIntoFirebase(sessionUser).then(() => {
                if (isMounted) setFirebaseReady(true);
              }).catch(err => {
                console.warn('[Firebase Auth] Initial sign-in failed:', err);
                if (isMounted) setFirebaseReady(true);
              });
              prefetchAllData(sessionUser.id);
            }
          } catch (err) {
            console.error('Error fetching user info:', err);
            if (isMounted) {
              setUser(enrichFromCache(sessionUser));
              // Sign into Firebase with basic user info
              signIntoFirebase(sessionUser).then(() => {
                if (isMounted) setFirebaseReady(true);
              }).catch(err => {
                console.warn('[Firebase Auth] Initial sign-in failed:', err);
                if (isMounted) setFirebaseReady(true);
              });
              prefetchAllData(sessionUser.id);
            }
          }
        } else if (isMounted) {
          setUser(null);
          setFirebaseReady(false);
          // Sign out of Firebase when Supabase session ends
          signOutOfFirebase();
        }
      } catch (error) {
        // Handle ReadableStream errors gracefully
        if (error.message?.includes('ReadableStream') || error.message?.includes('locked')) {
          console.warn('[Auth] Stream error in initSession, will retry via onAuthStateChange');
        } else {
          console.error('Session error:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    initSession();

    // Listen for auth changes including password recovery
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Skip if we just initialized to prevent duplicate calls
      if (isInitialized && event === 'INITIAL_SESSION') {
        return;
      }
      
      if (event === 'PASSWORD_RECOVERY') {
        setIsResetPassword(true);
      }

      // When Supabase confirms an email change, propagate the new email to all backends
      if (event === 'USER_UPDATED' && session?.user?.email) {
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const profileRes = await fetch(`${API_URL}/api/user/profile/${session.user.id}`);
          if (profileRes.ok) {
            const profileData = await profileRes.json();
            const storedEmail = profileData.email;
            const supabaseEmail = session.user.email;
            // Only sync if Supabase email differs from what's stored in MongoDB
            if (storedEmail && supabaseEmail && storedEmail.toLowerCase() !== supabaseEmail.toLowerCase()) {
              console.log('[Auth] Email change detected, syncing to all backends:', supabaseEmail);
              await fetch(`${API_URL}/api/user/profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  user_id: session.user.id,
                  name: profileData.name,
                  email: supabaseEmail,
                  birthday: profileData.birthday,
                  avatar_url: profileData.avatar_url
                })
              });
              console.log('[Auth] Email synced successfully to MongoDB, church_members, and Stripe');
            }
          }
        } catch (emailSyncErr) {
          console.warn('[Auth] Non-critical: email sync after verification failed:', emailSyncErr);
        }
      }
      
      if (session?.user) {
        // Enrich user with church info and role
        try {
          const API_URL = process.env.REACT_APP_BACKEND_URL;
          const userInfoResponse = await fetch(`${API_URL}/api/auth/user-info/${session.user.id}`);
          if (userInfoResponse.ok) {
            const userInfo = await userInfoResponse.json();
            const enrichedUser = {
              ...session.user,
              church_id: userInfo.church_id,
              church_name: userInfo.church_name,
              church_slug: userInfo.church_slug,
              church_created_at: userInfo.church_created_at,
              role: userInfo.role,
              web_access: userInfo.web_access,
              is_super_admin: userInfo.is_super_admin === true,
              name: userInfo.full_name || session.user.user_metadata?.full_name || session.user.user_metadata?.name || null,
              avatar: userInfo.avatar_url || session.user.user_metadata?.avatar_url,
              comms_user_id: session.user.id  // V2: Always use Supabase UUID
            };
            setUser(enrichedUser);
            identifyUser(enrichedUser);
            cacheUserEnrichment(session.user.id, {
              church_id: userInfo.church_id, church_name: userInfo.church_name,
              church_slug: userInfo.church_slug, church_created_at: userInfo.church_created_at,
              role: userInfo.role, web_access: userInfo.web_access, is_super_admin: userInfo.is_super_admin === true, name: enrichedUser.name, avatar: enrichedUser.avatar
            });
            // Sign into Firebase for Firestore security rules
            signIntoFirebase(enrichedUser).then(() => {
              if (isMounted) setFirebaseReady(true);
            }).catch(err => {
              console.warn('[Firebase Auth] Auth change sign-in failed:', err);
              if (isMounted) setFirebaseReady(true);
            });
            // Save userId to localStorage for push notification handlers
            localStorage.setItem('userId', session.user.id);
            // Pre-fetch data on login with church_id
            prefetchAllData(session.user.id, userInfo.church_id);
          } else if (isMounted) {
            setUser(enrichFromCache(session.user));
            // Sign into Firebase with basic user info
            signIntoFirebase(session.user).then(() => {
              if (isMounted) setFirebaseReady(true);
            }).catch(err => {
              console.warn('[Firebase Auth] Auth change sign-in failed:', err);
              if (isMounted) setFirebaseReady(true);
            });
            localStorage.setItem('userId', session.user.id);
            prefetchAllData(session.user.id);
          }
        } catch (err) {
          // Handle ReadableStream errors gracefully
          if (err.message?.includes('ReadableStream') || err.message?.includes('locked')) {
            console.warn('[Auth] Stream error in onAuthStateChange, session may need refresh');
          } else {
            console.error('Error fetching user info on auth change:', err);
          }
          if (isMounted) {
            setUser(enrichFromCache(session.user));
            // Sign into Firebase with basic user info
            signIntoFirebase(session.user).then(() => {
              if (isMounted) setFirebaseReady(true);
            }).catch(err => {
              console.warn('[Firebase Auth] Auth change sign-in failed:', err);
              if (isMounted) setFirebaseReady(true);
            });
            localStorage.setItem('userId', session.user.id);
            prefetchAllData(session.user.id);
          }
        }
      } else if (isMounted) {
        setUser(null);
        setFirebaseReady(false);
        // Sign out of Firebase when Supabase session ends
        signOutOfFirebase();
        const oldUserId = localStorage.getItem('userId');
        if (oldUserId) localStorage.removeItem(`user_enrichment_${oldUserId}`);
        localStorage.removeItem('userId');
      }
    });

    // Check if this is a password reset URL. Branching logic lives in a pure,
    // unit-tested helper (parseRecoveryFromUrl) — see recoveryLink.test.js.
    const recovery = parseRecoveryFromUrl(window.location.hash, window.location.search);

    if (recovery.kind === 'error') {
      // (1) Dead link (expired / already used / consumed by an email scanner).
      setRecoveryError(recovery.message);
      window.history.replaceState(null, document.title, window.location.pathname);
    } else if (recovery.kind === 'token_hash') {
      // (2) Scanner-resistant flow: verify via POST (inert to GET prefetchers).
      window.history.replaceState(null, document.title, window.location.pathname);
      supabase.auth.verifyOtp({ token_hash: recovery.tokenHash, type: 'recovery' })
        .then(({ error }) => {
          if (error) {
            setRecoveryError(RECOVERY_VERIFY_FAILED_MSG);
          } else {
            setIsResetPassword(true);
          }
        })
        .catch(() => setRecoveryError(RECOVERY_VERIFY_FAILED_MSG));
    } else if (recovery.kind === 'legacy') {
      // (3) Legacy implicit-flow recovery (hash tokens) — backward compatible.
      // If the session can't be established, the reset form MUST NOT render:
      // updateUser() would otherwise hit whatever stale session lives in
      // localStorage and change the wrong account's password.
      window.history.replaceState(null, document.title, window.location.pathname);
      supabase.auth.setSession({
        access_token: recovery.accessToken,
        refresh_token: recovery.refreshToken,
      }).then(({ error }) => {
        if (error) setRecoveryError(RECOVERY_VERIFY_FAILED_MSG);
        else setIsResetPassword(true);
      }).catch(() => setRecoveryError(RECOVERY_VERIFY_FAILED_MSG));
    } else if (recovery.kind === 'legacy_no_tokens') {
      // type=recovery with no tokens = consumed/mangled link. Never show the
      // form without a verified recovery session.
      window.history.replaceState(null, document.title, window.location.pathname);
      setRecoveryError(RECOVERY_VERIFY_FAILED_MSG);
    }

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [prefetchAllData]);

  // Clean up URL path after OAuth redirects (e.g., /planner?google_calendar=connected -> /)
  useEffect(() => {
    const path = window.location.pathname;
    if (path !== '/' && (path.startsWith('/planner') || path.startsWith('/settings') || path.startsWith('/services'))) {
      // Shared-item deep links are consumed (and cleaned) by PlannerApp itself,
      // which is lazy-loaded and mounts after this timer would have wiped them.
      if (window.location.search.includes('shared_highlight')) return;
      if (window.location.search.includes('service=')) return;
      // Keep query params for the component to handle, but clean up after a short delay
      setTimeout(() => {
        window.history.replaceState({}, document.title, '/');
      }, 100);
    }
  }, []);

  // Notification fetch and visibility changes are handled internally by useNotifications hook

  // Re-prefetch posts and conversations when user returns to tab
  useEffect(() => {
    if (!user?.id) return;
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        prefetchAllData(user.id, user.church_id);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user?.id, user?.church_id, prefetchAllData]);

  // Re-fetch profile data when tab regains focus to pick up changes made on
  // other devices (display name, avatar, role, church name).
  //
  // History: this used to call supabase.auth.refreshSession() which fired
  // TOKEN_REFRESHED → onAuthStateChange re-ran the enrichment block as a
  // side effect. That approach abused refresh tokens as a side channel for
  // a plain HTTP refetch, and it caused a refresh-token-rotation race when
  // a user had both web and mobile signed in: laptop sleeps → mobile rotates
  // the family → laptop wakes → stale refresh token → Supabase rejects →
  // SIGNED_OUT → user kicked to login. Now we just refetch profile data
  // directly via HTTP. No refresh tokens touched, no race. Supabase's
  // built-in autoRefreshToken handles JWT lifetime independently.
  useEffect(() => {
    if (!user?.id) return;
    let lastRefetchStarted = 0;
    let inFlight = false;
    const COOLDOWN_MS = 30_000;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      if (inFlight) return;
      const now = Date.now();
      if (now - lastRefetchStarted < COOLDOWN_MS) return;
      lastRefetchStarted = now;
      inFlight = true;
      try {
        const resp = await fetch(`${API_URL}/api/auth/user-info/${user.id}`);
        if (!resp.ok) return;
        const userInfo = await resp.json();
        // Build enriched user OUTSIDE setUser so the setter stays pure.
        let enrichedSnapshot = null;
        setUser(prev => {
          if (!prev || prev.id !== user.id) return prev;
          const enriched = {
            ...prev,
            church_id: userInfo.church_id,
            church_name: userInfo.church_name,
            church_slug: userInfo.church_slug,
            church_created_at: userInfo.church_created_at,
            role: userInfo.role,
            web_access: userInfo.web_access,
            is_super_admin: userInfo.is_super_admin === true,
            name: userInfo.full_name || prev.name,
            avatar: userInfo.avatar_url || prev.avatar,
          };
          enrichedSnapshot = enriched;
          return enriched;
        });
        // Side effects AFTER the setter has resolved. Setter must be pure
        // because React 18 strict mode (dev) invokes setters twice — doing
        // side effects inside would fire localStorage / analytics writes
        // twice in development.
        if (enrichedSnapshot) {
          // Sync the localStorage enrichment cache so a future cold start
          // renders with the latest data. Schema matches cacheUserEnrichment.
          try {
            localStorage.setItem(
              `user_enrichment_${user.id}`,
              JSON.stringify({
                church_id: enrichedSnapshot.church_id,
                church_name: enrichedSnapshot.church_name,
                church_slug: enrichedSnapshot.church_slug,
                church_created_at: enrichedSnapshot.church_created_at,
                role: enrichedSnapshot.role,
                web_access: enrichedSnapshot.web_access,
                is_super_admin: enrichedSnapshot.is_super_admin === true,
                name: enrichedSnapshot.name,
                avatar: enrichedSnapshot.avatar,
              })
            );
          } catch (_) { /* localStorage quota — ignore */ }
          // Keep PostHog properties fresh (role/name changes from mobile).
          try { identifyUser(enrichedSnapshot); } catch (_) { /* ignore */ }
          // Opportunistic Firebase re-sign-in if auth got dropped while
          // tab was hidden. Idempotent: short-circuits in firebase.js when
          // auth.currentUser.uid === user.id (the normal case).
          try { signIntoFirebase(enrichedSnapshot); } catch (_) { /* ignore */ }
        }
      } catch (_) {
        // Network blip or backend hiccup — silent. Next visibility tick retries.
      } finally {
        inFlight = false;
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user?.id]);

  // Subscription status check - check if church is blocked
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!user?.church_id) return;
      
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        // Pass user_email so the backend can surface a recovery_invite if
        // this user has a valid pending invite to another (non-blocked)
        // church — drives the "Switch to [Church]" button on the Access
        // Suspended screen. Backend never returns the raw invite token here.
        const emailParam = user?.email ? `?user_email=${encodeURIComponent(user.email)}` : '';
        const response = await fetch(`${API_URL}/api/stripe/subscription/${user.church_id}${emailParam}`);
        
        if (response.ok) {
          const data = await response.json();
          setSubscriptionStatus(data);
          // P2-l (DIV-042): drive the dashboard plan badge from the real tier.
          setUserPlan(data?.subscription?.tier || 'none');
          setIsBlocked(data.is_blocked || false);
          setIsPaused(data.is_paused || false);
          setChurchGone(false);
        } else if (response.status === 404) {
          // The church document is gone (deleted by a super admin). This is a
          // deterministic DB lookup — a 404 can only mean the church no longer
          // exists. Show the graceful "church no longer exists" screen.
          setChurchGone(true);
        }
      } catch (err) {
        console.error('Error checking subscription status:', err);
      }
    };
    
    checkSubscriptionStatus();

    // Re-check every 5 minutes so cancellations are caught promptly
    const interval = setInterval(checkSubscriptionStatus, 5 * 60 * 1000);

    // Re-check when the user returns to the tab
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkSubscriptionStatus();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user?.church_id]);

  // Quick Add Task handler - defined before useEffect so it can be referenced
  const handleQuickAddTask = useCallback(async (dayOffset) => {
    // Prevent double submission
    if (!quickAddText.trim() || !user || quickAddLoading) return;
    
    setQuickAddLoading(true);
    try {
      const targetDate = dayOffset === 0 ? new Date() : addDays(new Date(), 1);
      const newTask = {
        user_id: user.id,
        title: quickAddText.trim(),
        description: '',
        date: format(targetDate, 'yyyy-MM-dd'),
        time: null,
        priority: 'none',
        completed: false,
        is_recurring: false
      };
      
      const { data, error } = await supabase.from('tasks').insert([newTask]).select();
      
      if (error) throw error;
      
      // Update global tasks state so Dashboard and Planner show the new task immediately
      if (data && data[0]) {
        setGlobalTasks(prev => [...prev, data[0]]);
      }
      
      setQuickAddText('');
      setShowQuickAdd(false);
      setQuickAddSelectedIndex(0);
    } catch (err) {
      console.error('Error adding quick task:', err);
    } finally {
      setQuickAddLoading(false);
    }
  }, [quickAddText, user, quickAddLoading, setGlobalTasks]);

  // Handle confirmed delete from hover (Universal - works across all apps)
  const handleConfirmHoverDelete = useCallback(async (deleteScope) => {
    if (!itemToDelete) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const pushTaskDeleteUndo = (taskData) => {
      if (!taskData) return;
      // Strip client-only decorations (overdue rollup adds these) — they are
      // not real columns and would make the Supabase re-insert fail.
      const { isOverdue, originalDate, ...row } = taskData;
      pushUndo(`"${taskData.title}" deleted`, async () => {
        const { data, error: restoreErr } = await supabase.from('tasks').insert([row]).select().single();
        if (restoreErr) throw restoreErr;
        setGlobalTasks(prev => [...prev, data]);
      }, { toastMessage: 'Task deleted' });
    };
    
    try {
      if (itemToDelete.type === 'task') {
        const taskData = itemToDelete.data;
        const isRecurring = taskData?.is_recurring || taskData?.parent_task_id;

        if (isRecurring && deleteScope) {
          // Shared implementation with PlannerApp.deleteTask — lib/recurringTasks.js
          const result = await deleteRecurringSeries({
            supabase, task: taskData, scope: deleteScope, allTasks: globalTasks,
          });
          setGlobalTasks(prev => prev.filter(t => !result.shouldRemoveFromState(t)));
          if (deleteScope === 'single') {
            pushTaskDeleteUndo(taskData);
          } else {
            pushUndo(
              deleteScope === 'all'
                ? `"${taskData.title}" series deleted`
                : `"${taskData.title}" future occurrences deleted`,
              async () => {
                const restored = await restoreRecurringSeries({ supabase, ...result });
                setGlobalTasks(prev => {
                  const ids = new Set(prev.map(t => t.id));
                  return [...prev, ...restored.filter(r => !ids.has(r.id))];
                });
              },
              { toastMessage: deleteScope === 'all' ? 'Series deleted' : 'Future occurrences deleted' }
            );
          }
        } else {
          // Non-recurring task (original behavior)
          const { error } = await supabase.from('tasks').delete().eq('id', itemToDelete.id);
          if (error) throw error;
          setGlobalTasks(prev => prev.filter(t => t.id !== itemToDelete.id));
          pushTaskDeleteUndo(taskData);
        }
      } else if (itemToDelete.type === 'priority') {
        const priorityData = itemToDelete.data;
        const { error } = await supabase.from('priorities').delete().eq('id', itemToDelete.id);
        if (error) throw error;
        setGlobalPriorities(prev => prev.filter(p => p.id !== itemToDelete.id));
        if (priorityData) {
          const { isOverdue, ...prow } = priorityData;
          pushUndo(`"${priorityData.title}" deleted`, async () => {
            const { data, error: restoreErr } = await supabase.from('priorities').insert([prow]).select().single();
            if (restoreErr) throw restoreErr;
            setGlobalPriorities(prev => [...prev, data]);
          }, { toastMessage: 'Priority deleted' });
        }
      } else if (itemToDelete.type === 'goal') {
        const goalData = itemToDelete.data;
        const { error } = await supabase.from('annual_goals').delete().eq('id', itemToDelete.id);
        if (error) throw error;
        setGlobalGoals(prev => prev.filter(g => g.id !== itemToDelete.id));
        if (goalData) {
          const { isOverdue, ...grow } = goalData;
          pushUndo(`"${goalData.title}" deleted`, async () => {
            const { data, error: restoreErr } = await supabase.from('annual_goals').insert([grow]).select().single();
            if (restoreErr) throw restoreErr;
            setGlobalGoals(prev => [...prev, data]);
          }, { toastMessage: 'Goal deleted' });
        }
      } else if (typeof itemToDelete.deleteFn === 'function') {
        // Items that own their delete: weekly priorities (local state) and shared
        // tasks/priorities/goals (Mongo-backed via useSharedItems, so none of the
        // Supabase branches above apply). The view registering the hover decides
        // who may delete; the fn itself reverts and toasts on failure.
        await itemToDelete.deleteFn();
      } else if (itemToDelete.type === 'post') {
        // Delete post via API - requires user_id
        const response = await fetch(`${API_URL}/api/comms/v3/posts/${itemToDelete.id}?user_id=${user.id}`, {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete post');
        setGlobalPosts(prev => prev.filter(p => p.id !== itemToDelete.id));
      } else if (itemToDelete.type === 'message' || itemToDelete.type === 'conversation') {
        // P2-h (DIV-032): the DELETE routes these branches called never existed.
        // DMs live in Firestore and are deleted from the Messages screen
        // (delete-for-everyone / leave). Hover-delete no longer offers them.
        throw new Error('Delete messages from the Messages screen');
      }
    } catch (err) {
      console.error('Error deleting item:', err);
    }
    
    setShowDeleteConfirm(false);
    setItemToDelete(null);
    setHoveredItem(null);
  }, [itemToDelete, user, setGlobalTasks, setGlobalPriorities, setGlobalGoals, setGlobalPosts, setGlobalConversations]);

  // Global keyboard shortcut for Quick Add Todo (CMD+Space), Delete, and CMD+Enter for dialogs
  useEffect(() => {
    const handleKeyDown = (e) => {
      // CMD/Ctrl + Space to toggle Quick Add (open/close)
      if ((e.metaKey || e.ctrlKey) && e.code === 'Space') {
        e.preventDefault();
        if (user) {
          if (showQuickAdd) {
            setShowQuickAdd(false);
            setQuickAddText('');
            setQuickAddSelectedIndex(0);
          } else {
            setShowQuickAdd(true);
            setQuickAddSelectedIndex(0);
          }
        }
      }
      // CMD/Ctrl + Enter to submit any open dialog
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (showQuickAdd && quickAddText.trim() && !quickAddLoading) {
          handleQuickAddTask(quickAddSelectedIndex);
        }
        if (showDeleteConfirm && itemToDelete) {
          // Don't auto-confirm recurring task deletes — user must pick a scope
          const isRecurringTask = itemToDelete.type === 'task' &&
            (itemToDelete.data?.is_recurring || itemToDelete.data?.parent_task_id);
          if (!isRecurringTask) {
            handleConfirmHoverDelete();
          }
        }
      }
      // Close Quick Add on Escape
      if (e.key === 'Escape' && showQuickAdd) {
        setShowQuickAdd(false);
        setQuickAddText('');
        setQuickAddSelectedIndex(0);
      }
      // Tab key to switch between Today/Tomorrow in Quick Add
      if (e.key === 'Tab' && showQuickAdd) {
        e.preventDefault();
        setQuickAddSelectedIndex(prev => prev === 0 ? 1 : 0);
      }
      // Delete on hover - UNIVERSAL across all apps (Dashboard, Planner, etc.) EXCEPT Team Feed posts.
      // Skip when focus is in a text field — otherwise Backspace while typing in any
      // dialog/input would trigger hover-delete of the previously hovered item.
      if ((e.key === 'Delete' || e.key === 'Backspace') && hoveredItem && !['post', 'message', 'conversation'].includes(hoveredItem.type) && !showDeleteConfirm) {
        const ae = document.activeElement;
        if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) return;
        e.preventDefault();
        setItemToDelete(hoveredItem);
        setShowDeleteConfirm(true);
      }
      // Close delete confirm on Escape
      if (e.key === 'Escape' && showDeleteConfirm) {
        setShowDeleteConfirm(false);
        setItemToDelete(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [user, showQuickAdd, quickAddText, quickAddSelectedIndex, quickAddLoading, hoveredItem, showDeleteConfirm, itemToDelete, handleQuickAddTask, handleConfirmHoverDelete]);

  const handleSignOut = async () => {
    resetUser();
    await signOut();
    await signOutOfFirebase();
    // Clear all cached data to prevent cross-church data leaks
    localStorage.removeItem('cache_posts');
    localStorage.removeItem('cache_recentComms');
    localStorage.removeItem('cache_tasks');
    try { Object.keys(localStorage).filter((k) => k.startsWith('post_draft_')).forEach((k) => localStorage.removeItem(k)); } catch { /* ignore */ } // R39 post drafts
    localStorage.removeItem('cache_church_id');
    localStorage.removeItem('plannerWeekStart');   // per-user planner preferences (R46)
    localStorage.removeItem('plannerPanelMode');
    setUserPlan('free');
    setGlobalPosts([]);
    setGlobalConversations([]);
    setGlobalRecentComms([]);
    // Reset Quick Compose state — prevents User A's open modal / PCO context
    // from leaking into User B's session after logout/login on the same tab.
    setShowQuickCompose(false);
    setPcoServicePlan(null);
    // Fix 7c F1: no comms or Services landing (a tapped service, a conversation, a
    // highlighted post) carries over to the next user who signs in on this tab.
    setCommsServiceHighlight(null);
    setCommsInitialTab(null);
    setCommsInitialConversationId(null);
    setCommsHighlightPostId(null);
    setCommsHighlightCommentId(null);
    setCommsHighlightMessageId(null);
    setServicesInitial(null);
    pendingSvcTapRef.current = null;
    setUser(null);
    setFirebaseReady(false);
    setCurrentApp(null);
  };

  // `options` (D18, 121 §5) is the one trailing, optional argument: an object with
  // extras a caller may name, today only `{ conversationId }` for the AI app. It
  // defaults to undefined, so every existing call site — all of them classic —
  // behaves exactly as before.
  const handleOpenApp = (appId, view = null, conversationOrMessage = null, highlightPostId = null, autoSubmit = true, highlightCommentId = null, highlightMessageId = null, options = undefined) => {
    trackEvent('app_opened', { app: appId, view });
    if (appId === 'planner') {
      if (view) {
        setPlannerView(view);
      }
      setCurrentApp('planner');
    } else if (appId === 'aichat') {
      setInitialAIMessage(conversationOrMessage || '');
      setAutoSubmitAI(autoSubmit && !!conversationOrMessage);
      setInitialAIConversationId(options?.conversationId || null);
      setCurrentApp('aichat');
    } else if (appId === 'communication' || appId === 'comms') {
      setCommsInitialTab(view); // 'feed' or 'messages'
      setCommsServiceHighlight(view === 'services' ? (options?.serviceHighlight || null) : null);   // Fix 7 G4
      // If view is 'messages' and conversationOrMessage is provided, treat it as conversation ID
      if (view === 'messages' && conversationOrMessage) {
        setCommsInitialConversationId(conversationOrMessage);
        setCommsHighlightPostId(null);
        setCommsHighlightMessageId(highlightMessageId);
        setCommsHighlightCommentId(null);
      } else if (view === 'feed' && highlightPostId) {
        // If view is 'feed' and highlightPostId is provided, highlight that post
        setCommsHighlightPostId(highlightPostId);
        setCommsHighlightCommentId(highlightCommentId);
        setCommsHighlightMessageId(null);
        setCommsInitialConversationId(null);
      } else {
        setCommsHighlightPostId(conversationOrMessage); // fallback: post ID to highlight
        setCommsHighlightCommentId(null);
        setCommsHighlightMessageId(null);
        setCommsInitialConversationId(null);
      }
      setCurrentApp('comms');
    } else if (appId === 'onboarding') {
      setOnboardingHighlightCandidateId(view); // view = candidateId to highlight
      setCurrentApp('onboarding');
    } else if (appId === 'messaging') {
      setCurrentApp('messaging');
    } else if (appId === 'tutorials') {
      // Push the hidden URL so browser Back returns to the dashboard and a
      // refresh keeps the user on the tutorials page.
      if (window.location.pathname !== '/tutorials') {
        window.history.pushState({}, '', '/tutorials');
      }
      setCurrentApp('tutorials');
    } else if (appId === 'services') {
      // view = { tab: 'plans'|'songs'|'people'|'settings', serviceId?: string }
      setServicesInitial(view || null);
      if (window.location.pathname !== '/services') window.history.pushState({}, '', '/services');
      setCurrentApp('services');
    } else {
      // Other apps coming soon
      alert('This feature is coming soon!');
    }
  };

  // Fix 7 G4: a Services notification tap. An admin (the /mode answer; while
  // unknown, role owner/admin) lands in the Services app as before; anyone else
  // in Team Comms on the Services view with that service highlighted.
  const openServiceNotification = (serviceId, type) => {
    if (!svcUserRef.current) {   // Fix 7c F16: signed out or still loading: route once the user is known
      pendingSvcTapRef.current = { serviceId: serviceId || null, type: type || null };
      return;
    }
    if (servicesAdminFor(svcUserRef.current)) {
      handleOpenApp('services', { tab: 'plans', serviceId: serviceId || null });
    } else {
      handleOpenApp('comms', 'services', null, null, true, null, null, { serviceHighlight: { serviceId: serviceId || null, type: type || null } });
    }
  };
  useEffect(() => {
    if (!user || !('role' in user) || !pendingSvcTapRef.current) return;
    const tap = pendingSvcTapRef.current;
    pendingSvcTapRef.current = null;
    openServiceNotification(tap.serviceId, tap.type);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleOpenSettings = (settingsPage) => {
    trackEvent('settings_opened', { page: settingsPage });
    setCurrentSettings(settingsPage);
  };

  const handleCloseSettings = () => {
    setCurrentSettings(null);
  };

  // Handle service worker messages (notification clicks from background push)
  useEffect(() => {
    if (!navigator.serviceWorker) return;
    
    const handleSWMessage = (event) => {
      const data = event.data;
      if (data?.type === 'NOTIFICATION_CLICK') {
        const notifData = data.data || {};
        const type = notifData.type;
        
        if (type === 'dm' || type === 'message_reaction') {
          handleOpenApp('comms', 'messages', notifData.conversation_id, null, true, null, notifData.message_id);
        } else if (['post', 'comment', 'reply', 'post_reaction', 'comment_reaction'].includes(type)) {
          handleOpenApp('comms', 'feed', null, notifData.post_id, true, notifData.comment_id);
        } else if (['service_request','service_response','service_updated','service_comment'].includes(type)) {
          openServiceNotification(notifData.service_id, type);
        } else if (notifData.url) {
          // Generic deep link (e.g. quarterly workshop page)
          spaNavigate(notifData.url);
        }
      }
    };
    
    navigator.serviceWorker.addEventListener('message', handleSWMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleSWMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Desktop app (Tauri shell v1.0.2+): native notification banner clicks →
  // deep-link to the exact post/comment/DM/message, mirroring the mobile
  // push (SW) router above and the in-app bell routing in Dashboard.js.
  const desktopPendingCheckedRef = useRef(false);
  useEffect(() => {
    if (!isDesktopApp()) return undefined;
    const routeDesktopNotif = (n) => {
      if (!n || typeof n !== 'object') return;
      if (n.id) notificationHook?.markReadByIds?.([n.id]);
      if (n.type === 'planner_review') {
        try { sessionStorage.setItem('plannerStartReview', '1'); } catch { /* ignore */ }
        handleOpenApp('planner', 'week');
      } else if (n.type === 'workshop') {
        if (n.workshop_url) spaNavigate(n.workshop_url);
        else if (user?.church_slug) spaNavigate(`/${user.church_slug}/training/workshop`);
      } else if (['service_request','service_response','service_updated','service_comment'].includes(n.type)) {
        openServiceNotification(n.service_id, n.type);
      } else if (n.type === 'dm' || n.type === 'message_reaction' || (n.conversation_id && !n.post_id)) {
        handleOpenApp('comms', 'messages', n.conversation_id || null, null, true, null, n.message_id || null);
      } else if (n.post_id) {
        handleOpenApp('comms', 'feed', null, n.post_id, true, n.comment_id || null, null);
      } else {
        handleOpenApp('comms', 'feed');
      }
    };
    const parseAndRoute = (payload) => {
      try {
        routeDesktopNotif(typeof payload === 'string' ? JSON.parse(payload) : payload);
      } catch (err) {
        console.error('Desktop notification deep-link failed:', err);
      }
    };
    // Cold-launch: the app may have been opened BY a notification click that
    // fired before this listener existed — collect it once the user is ready.
    if (user && !desktopPendingCheckedRef.current) {
      desktopPendingCheckedRef.current = true;
      takePendingDesktopDeepLink().then((link) => { if (link) parseAndRoute(link); });
      // Surface OS-level notification muting (e.g. "Don't Allow" clicked on
      // the macOS permission prompt) — banners silently vanish otherwise.
      getDesktopNotificationPermission().then((state) => {
        if (state === 'denied') {
          import('sonner').then(({ toast }) => {
            toast('Desktop notifications are turned off', {
              description: 'Enable them in System Settings → Notifications → Worship OS to get message banners.',
              duration: 12000,
            });
          }).catch(() => {});
        }
      });
    }
    return onDesktopNotificationClick(parseAndRoute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.church_slug]);

  const handleUpdateUser = (updatedUser) => {
    // Merge over the previous user so partial profile updates never drop
    // enrichment fields like role / web_access / church info.
    setUser(prev => {
      const merged = prev ? { ...prev, ...updatedUser } : updatedUser;
      // Persist enrichment cache so next page load gets fresh data instantly
      try {
        localStorage.setItem(`user_enrichment_${merged.id}`, JSON.stringify({
          church_id: merged.church_id,
          church_name: merged.church_name,
          church_slug: merged.church_slug,
          church_created_at: merged.church_created_at,
          role: merged.role,
          web_access: merged.web_access,
          is_super_admin: merged.is_super_admin === true,
          name: merged.name,
          avatar: merged.avatar
        }));
      } catch (e) { /* localStorage may be unavailable */ }
      return merged;
    });
  };

  // Auth handoff from AuthPage. Enrich the freshly-authenticated user BEFORE
  // committing it to state, so the web-access gate never evaluates against a
  // role-less raw Supabase user (which previously caused a granted team member
  // to be stuck on the Download App screen until a manual reload). onAuthStateChange
  // also enriches as a redundant fallback; both produce the same enriched object.
  const handleAuthSuccess = async (sessionUser) => {
    if (!sessionUser) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      const resp = await fetch(`${API_URL}/api/auth/user-info/${sessionUser.id}`);
      if (resp.ok) {
        const userInfo = await resp.json();
        const enriched = {
          ...sessionUser,
          church_id: userInfo.church_id,
          church_name: userInfo.church_name,
          church_slug: userInfo.church_slug,
          church_created_at: userInfo.church_created_at,
          role: userInfo.role,
          web_access: userInfo.web_access,
          is_super_admin: userInfo.is_super_admin === true,
          name: userInfo.full_name || sessionUser.user_metadata?.full_name || sessionUser.user_metadata?.name || null,
          avatar: userInfo.avatar_url || sessionUser.user_metadata?.avatar_url,
          comms_user_id: sessionUser.id,
        };
        setUser(enriched);
        try { identifyUser(enriched); } catch (_) { /* ignore */ }
        try {
          localStorage.setItem(`user_enrichment_${sessionUser.id}`, JSON.stringify({
            church_id: enriched.church_id, church_name: enriched.church_name,
            church_slug: enriched.church_slug, church_created_at: enriched.church_created_at,
            role: enriched.role, web_access: enriched.web_access, is_super_admin: enriched.is_super_admin === true, name: enriched.name, avatar: enriched.avatar,
          }));
        } catch (_) { /* ignore */ }
        return;
      }
    } catch (_) { /* fall through to cache/raw fallback */ }
    // Fallback (rare): commit from cache so login still completes; the
    // onAuthStateChange listener will re-enrich shortly.
    try {
      const cached = JSON.parse(localStorage.getItem(`user_enrichment_${sessionUser.id}`) || 'null');
      setUser(cached ? { ...sessionUser, ...cached, comms_user_id: sessionUser.id } : sessionUser);
    } catch (_) {
      setUser(sessionUser);
    }
  };

  // Detect Planning Center connection callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('pc_connected') === 'true' && user) {
      setShowPCConnectedPrompt(true);
      // Clean the URL
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [user]);

  const handleResetComplete = () => {
    setIsResetPassword(false);
    // Clear the URL hash
    window.history.replaceState(null, '', window.location.pathname);
  };

  if (loading) {
    return (
      <div data-ui="page" className="min-h-screen bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] flex items-center justify-center">
        <div className="text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_praiseleadsys/artifacts/dqkpm4bx_Untitled%20design%20%284%29.png" 
            alt="Worship OS" 
            data-ui="boot-mark"
            className="w-16 h-16 rounded-2xl object-contain mx-auto mb-4 animate-pulse"
          />
          <p className="text-slate-500 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Public routes (no auth required)
  const path = window.location.pathname;
  const hostname = window.location.hostname;

  // WEB ACCESS GATE — single source of truth. Only owners/admins/super-admins
  // may use the web app; everyone else (team_member / unknown role) is kept on
  // mobile. Computed once and reused by the /team route and the root gate below
  // so the privilege rule can never drift between the two.
  const isPrivileged = !!user && (
    user.role === 'owner' || user.role === 'admin' || user.is_super_admin === true
  );

  // Per-user override: a team member without a smartphone can be granted web
  // access (Team Comms only) by an owner/admin. This re-enables the team-member
  // web view for that one user — it does NOT grant admin tools or extra data.
  const hasWebAccess = !!user && user.web_access === true;

  // Landing page - show for:
  // 1. /landing route on any domain
  // 2. Root path on worshipos.app (not go.worshipos.app)
  const isLandingDomain = hostname === 'worshipos.app' || hostname === 'www.worshipos.app';
  const isLandingRoute = path === '/landing' || path === '/landing/';
  const isRootOnLandingDomain = isLandingDomain && (path === '/' || path === '');
  
  if (isLandingRoute || isRootOnLandingDomain) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <LandingPage />
      </Suspense>
    );
  }
  
  // New church signup page (no auth required)
  if (path === '/signup' || path === '/signup/') {
    const urlParams = new URLSearchParams(window.location.search);
    const canceled = urlParams.get('canceled');
    
    return (
      <ChurchSignupPage 
        onBack={() => window.location.href = '/'}
        wasCanceled={canceled === 'true'}
      />
    );
  }
  
  // Signup success page (polling for completion)
  if (path === '/signup-success' || path === '/signup-success/') {
    return (
      <SignupSuccessPage
        onComplete={() => window.location.href = '/'}
        onLogin={(email) => {
          // Redirect to login with pre-filled email
          window.location.href = '/?email=' + encodeURIComponent(email);
        }}
      />
    );
  }
  
  // Billing success page (redirect from Stripe checkout)
  if (path === '/billing-success' || path === '/billing-success/') {
    if (!user) {
      return <AuthPage onAuth={handleAuthSuccess} redirectTo="/billing-success" />;
    }
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <BillingSuccessPage 
          onComplete={() => {
            // P2-g (DIV-031): land on the Billing page so the user sees the new
            // subscription. Settings pages render from `currentSettings`, not
            // `currentApp` (which has no 'settings' branch).
            window.history.replaceState({}, document.title, '/settings/billing');
            setCurrentSettings('billing');
          }}
        />
      </Suspense>
    );
  }
  
  // AI Credit refill success (same flow as billing success)
  if (path === '/refill-success' || path === '/refill-success/') {
    if (!user) {
      return <AuthPage onAuth={handleAuthSuccess} redirectTo="/refill-success" />;
    }
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <BillingSuccessPage 
          onComplete={() => {
            // Return to AI Chat where user can use their new credits
            window.history.replaceState({}, document.title, '/worship-ai');
            setCurrentApp('aichat');
          }} 
        />
      </Suspense>
    );
  }
  
  // Public intake form: /intake/:churchSlug  OR  /intake/:churchSlug/:trackSlug (BETA)
  if (path.startsWith('/intake/')) {
    const rest = path.split('/intake/')[1] || '';
    const parts = rest.split('/').filter(Boolean);
    const churchSlug = parts[0];
    const trackSlug = parts[1] || null;
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <PublicIntakeForm churchSlug={churchSlug} trackSlug={trackSlug} />
      </Suspense>
    );
  }
  
  // Public onboarding documents: /onboarding-docs/:token
  if (path.startsWith('/onboarding-docs/')) {
    const token = path.split('/onboarding-docs/')[1];
    // Lazy load component to avoid issues
    const PublicOnboardingDocs = lazy(() => import('./components/onboarding/PublicOnboardingDocs'));
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <PublicOnboardingDocs token={token} />
      </Suspense>
    );
  }
  
  // Public training page: /training/:churchSlug/:instrument?token=xxx
  if (path.startsWith('/training/')) {
    // Token is in query parameter, not the path
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    // Instrument slug from the path — needed so multi-position candidates see
    // the position this specific email linked to (not just their primary one).
    const trainingInstrument = path.split('/')[3] ? decodeURIComponent(path.split('/')[3]) : null;
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <PublicTrainingPage token={token} instrument={trainingInstrument} />
      </Suspense>
    );
  }

  // Admin "Preview as candidate": /preview/training/:churchId/:instrument
  // Renders the exact candidate training experience (read-only) for an instrument
  // or custom position, without a candidate token. Public so leaders can share it.
  if (path.startsWith('/preview/training/')) {
    const segs = path.replace(/^\/preview\/training\//, '').split('?')[0].split('/').filter(Boolean);
    const previewChurchId = segs[0];
    const previewInstrument = segs[1];
    const previewTrackId = new URLSearchParams(window.location.search).get('track_id');
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <PublicTrainingPage preview={{ churchId: previewChurchId, instrument: previewInstrument, trackId: previewTrackId }} />
      </Suspense>
    );
  }
  
  // Public audition prep page: /audition-prep/:churchSlug
  if (path.startsWith('/audition-prep/')) {
    const churchSlug = path.split('/audition-prep/')[1].split('?')[0];
    const auditionTrack = new URLSearchParams(window.location.search).get('track');
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <PublicAuditionPrepPage churchSlug={churchSlug} track={auditionTrack} />
      </Suspense>
    );
  }

  // Public standalone single-video page: /{slug}/training/{instrument}/v/{ref}
  // Must run BEFORE the 2-segment library match below. Shares the same RESERVED
  // first-segment guard so it never steals top-level routes.
  {
    const videoMatch = path.match(/^\/([^/]+)\/training\/([^/]+)\/v\/([^/?#]+)\/?$/);
    if (videoMatch) {
      const [, vChurchSlug, vInstrumentSlug, vRef] = videoMatch;
      const RESERVED_FIRST_SEGMENTS = new Set([
        'settings', 'planner', 'onboarding', 'team', 'training',
        'intake', 'audition-prep', 'onboarding-docs', 'invite',
        'signup', 'signup-success', 'billing-success', 'refill-success',
        'landing', 'api', 'admin', 'auth',
      ]);
      if (!RESERVED_FIRST_SEGMENTS.has(vChurchSlug)) {
        const vTeamId = new URLSearchParams(window.location.search).get('team');
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <TrainingVideoPage
              churchSlug={vChurchSlug}
              instrumentSlug={vInstrumentSlug}
              videoRef={vRef}
              teamId={vTeamId}
            />
          </Suspense>
        );
      }
    }
  }

  // Public training library: /{slug}/training and /{slug}/training/{instrument}
  // NOTE: must run AFTER all existing single-prefix public routes (/training/,
  // /intake/, /audition-prep/, /onboarding-docs/) so we never steal their traffic,
  // and BEFORE the auth gate so unauthenticated visitors can reach the library.
  // The RESERVED set guards against catching paths like /settings/calendar.
  {
    const trainingLibMatch = path.match(/^\/([^/]+)\/training(?:\/([^/?#]+))?\/?$/);
    if (trainingLibMatch) {
      const trainingChurchSlug = trainingLibMatch[1];
      const trainingInstrumentSlug = trainingLibMatch[2];
      const RESERVED_FIRST_SEGMENTS = new Set([
        'settings', 'planner', 'onboarding', 'team', 'training',
        'intake', 'audition-prep', 'onboarding-docs', 'invite',
        'signup', 'signup-success', 'billing-success', 'refill-success',
        'landing', 'api', 'admin', 'auth',
      ]);
      if (!RESERVED_FIRST_SEGMENTS.has(trainingChurchSlug)) {
        const trainingTeam = new URLSearchParams(window.location.search).get('team');
        return (
          <Suspense fallback={
            <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          }>
            {!trainingInstrumentSlug ? (
              <TrainingLibraryHome churchSlug={trainingChurchSlug} user={user} />
            ) : trainingInstrumentSlug === 'workshop' ? (
              <FeaturedWorkshopPage churchSlug={trainingChurchSlug} />
            ) : trainingInstrumentSlug === 'team-docs' ? (
              <TeamDocsPage churchSlug={trainingChurchSlug} teamId={trainingTeam} />
            ) : (
              <TrainingInstrumentPage
                churchSlug={trainingChurchSlug}
                instrumentSlug={trainingInstrumentSlug}
                user={user}
                teamId={trainingTeam}
              />
            )}
          </Suspense>
        );
      }
    }
  }

  // Team member view: /team
  // WEB ACCESS GATE: non-admin team members are kept on mobile. The ONLY web
  // exception is the "Important Info" post+email deep link (/team?post=<id>),
  // which renders a read-only single-post view (no tabs/feed/messages). Every
  // other /team access for a team member routes to the Download App screen.
  // Admins / owners / super-admins are unaffected and get the full TeamMemberView.
  if (path === '/team' || path === '/team/') {
    if (!user) {
      return <AuthPage onAuth={handleAuthSuccess} hideSignupOption={true} />;
    }

    // Parse URL params for deep linking (from notifications / Important Info email)
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    const postParam = urlParams.get('post');
    const commentParam = urlParams.get('comment');
    const conversationParam = urlParams.get('conversation');
    const msgParam = urlParams.get('msg');
    // NOTE: notif_id is handled in the useEffect above, not here
    // to avoid fetch calls in render

    if (!isPrivileged && !hasWebAccess) {
      // The single allowed web exception for non-granted members: the
      // Important Info post link.
      if (postParam) {
        return (
          <Suspense fallback={<LoadingSpinner />}>
            <ImportantPostView user={user} postId={postParam} onSignOut={handleSignOut} />
          </Suspense>
        );
      }
      return <DownloadAppGate email={user?.email} onSignOut={handleSignOut} />;
    }

    return (
      <TeamMemberView 
        user={user} 
        onLogout={handleSignOut}
        globalPosts={globalPosts}
        setGlobalPosts={setGlobalPosts}
        globalTasks={globalTasks}
        setGlobalTasks={setGlobalTasks}
        notificationHook={notificationHook}
        markMessagesAsRead={markMessagesAsRead}
        initialTab={tabParam || commsInitialTab}
        initialConversationId={conversationParam || commsInitialConversationId}
        highlightPostId={postParam || commsHighlightPostId}
        highlightCommentId={commentParam || commsHighlightCommentId}
        highlightMessageId={msgParam || commsHighlightMessageId}
        serviceHighlight={commsServiceHighlight}
      />
    );
  }
  
  if (path.startsWith('/respond/')) { return (<Suspense fallback={<LoadingSpinner />}><PublicRespondPage token={path.split('/')[2] || ''} /></Suspense>); }
  // Invite link: /invite/:token - only show if not logged in
  if (path.startsWith('/join/')) {
    const joinToken = (path.split('/join/')[1] || '').split('/')[0];
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <JoinByLinkPage token={joinToken} />
      </Suspense>
    );
  }

  if (path.startsWith('/invite/') && !user) {
    const inviteToken = path.split('/invite/')[1];
    return <AuthPage onAuth={handleAuthSuccess} inviteToken={inviteToken} />;
  }

  // Logged-in user hitting /invite/:token — could be:
  //   (a) the just-signed-up team member whose AuthPage flow brought us here
  //   (b) an already-logged-in user clicking a new invite link (church switch,
  //       re-invite after their old church canceled, etc.)
  // InviteAcceptHandler calls setup-account with the new token; the backend
  // returns idempotently for case (a) and performs the switch for case (b),
  // then we hard-refresh into the dashboard. See backend matrix in
  // routes/auth.py::setup_account.
  if (path.startsWith('/invite/') && user) {
    const inviteToken = path.split('/invite/')[1];
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <InviteAcceptHandler user={user} inviteToken={inviteToken} />
      </Suspense>
    );
  }

  // Show reset password form if user clicked the email link
  if (isResetPassword) {
    return <ResetPasswordPage onComplete={handleResetComplete} />;
  }

  if (!user) {
    return <AuthPage onAuth={handleAuthSuccess} recoveryError={recoveryError} onRecoveryErrorClear={() => setRecoveryError('')} />;
  }

  // Church deleted (super admin removed it) but this session is still alive —
  // without this gate the user sits in a broken app shell polling 404s forever.
  if (churchGone) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#1e1f22] flex items-center justify-center p-4" data-testid="church-gone-page">
        <div className="max-w-md w-full text-center bg-white dark:bg-[#2b2d31] border border-slate-200 dark:border-white/10 rounded-xl shadow-lg px-8 py-10">
          <div className="mx-auto w-16 h-16 rounded-full bg-slate-100 dark:bg-white/5 flex items-center justify-center mb-5">
            <Building2 className="w-9 h-9 text-slate-400 dark:text-slate-500" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 mb-3">
            This church no longer exists
          </h1>
          <p className="text-slate-600 dark:text-slate-300 mb-2">
            The church linked to your account has been removed from Worship OS.
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
            If you believe this is a mistake, contact{' '}
            <a href="mailto:support@worshipos.app" className="text-sky-600 dark:text-sky-400 hover:underline">support@worshipos.app</a>.
          </p>
          <Button onClick={handleSignOut} data-testid="church-gone-signout-btn">
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  // Check if church is blocked (subscription canceled/failed payment)
  // Super admins bypass this check. Blocked takes priority over paused —
  // if both flags ever coexist, the actionable reactivate screen must win.
  if (isBlocked && !subscriptionStatus?.is_super_admin) {
    const isAdmin = user.role === 'owner' || user.role === 'admin';
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <BlockedUserPage
          user={user}
          isAdmin={isAdmin}
          ownerEmail={subscriptionStatus?.owner_email}
          previousTier={subscriptionStatus?.subscription?.tier}
          recoveryInvite={subscriptionStatus?.recovery_invite}
        />
      </Suspense>
    );
  }

  // Subscription paused by a super admin — friendly screen (NOT the blocked
  // screen). Super admins bypass so they can still administer.
  if (isPaused && !subscriptionStatus?.is_super_admin) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <SubscriptionPausedPage user={user} pausedUntil={subscriptionStatus?.paused_until} />
      </Suspense>
    );
  }

  // WEB ACCESS GATE (root + all admin app routes): non-admin team members are
  // kept on mobile — they get the Download App screen, never the web app. The
  // only web exceptions: (1) owners/admins/super-admins get the full app, and
  // (2) a team member explicitly granted web access gets Team Comms ONLY (no
  // admin tools). The Important Info /team?post=<id> link is handled above.
  if (!isPrivileged) {
    if (hasWebAccess) {
      return (
        <Suspense fallback={<LoadingSpinner />}>
          <TeamCommsApp 
            user={user} 
            onBack={handleSignOut}
            highlightPostId={commsHighlightPostId}
            globalPosts={globalPosts}
            setGlobalPosts={setGlobalPosts}
            setHoveredItem={setHoveredItem}
            isTeamMemberView={true}
            onUpdateUser={handleUpdateUser}
            notificationHook={notificationHook}
            markMessagesAsRead={markMessagesAsRead}
            archivedConversationIds={archivedConversationIds}
            serviceHighlight={commsServiceHighlight}
          />
        </Suspense>
      );
    }
    return <DownloadAppGate email={user?.email} onSignOut={handleSignOut} />;
  }


  // Quick Add Todo Modal - rendered on top of any screen when user is logged in
  const QuickAddModal = (
    <Dialog open={showQuickAdd} onOpenChange={(open) => { setShowQuickAdd(open); if (!open) { setQuickAddText(''); setQuickAddSelectedIndex(0); } }}>
      <DialogContent className={`${isStudioDesign() ? 'sm:max-w-[560px]' : 'sm:max-w-lg'} p-0 gap-0 overflow-hidden border-0 shadow-xl bg-card rounded-xl`}>
        <div className={isStudioDesign() ? 'px-5 py-4' : 'px-6 py-5'}>
          <div className={isStudioDesign() ? 'mb-4' : 'flex items-center gap-3 mb-5'}>
            {!isStudioDesign() && (
            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
              <Plus className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            )}
            <div>
              <h3 className={isStudioDesign() ? 'font-semibold tracking-tight text-foreground text-lg' : 'font-semibold text-slate-800 dark:text-slate-100 text-lg'}>{isStudioDesign() ? 'Quick add task' : 'Quick Add Task'}</h3>
              <p className={isStudioDesign() ? 'text-[13.5px] text-muted-foreground mt-1' : 'text-xs text-slate-400 dark:text-slate-500'}>⌘ / Ctrl + Space to toggle</p>
            </div>
          </div>
          <Input
            autoFocus
            placeholder="What needs to be done?"
            value={quickAddText}
            onChange={(e) => setQuickAddText(e.target.value)}
            onKeyDown={(e) => {
              // Enter submits with selected button (CMD+Enter handled globally)
              if (e.key === 'Enter' && quickAddText.trim() && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                handleQuickAddTask(quickAddSelectedIndex);
              }
            }}
            className={isStudioDesign() ? 'text-sm h-10 rounded-lg' : 'text-base h-12 border-slate-200 dark:border-white/10 focus:border-indigo-400 focus:ring-indigo-400 rounded-xl'}
            disabled={quickAddLoading}
          />
          <div className="flex gap-3 mt-4">
            {/* O7 (120): Today and Tomorrow are the same kind of choice, so in Studio
                they are peers — two outline buttons, the selected one carrying the
                kit's `fg` ring. A filled primary here read as the dialog's primary
                action. Classic keeps its exact variant and class string. */}
            <Button
              onClick={() => handleQuickAddTask(0)}
              disabled={!quickAddText.trim() || quickAddLoading}
              variant={isStudioDesign() ? 'outline' : undefined}
              className={isStudioDesign()
                ? `flex-1 h-10 font-medium rounded-lg text-sm ${quickAddSelectedIndex === 0 ? 'border-foreground' : ''}`
                : `flex-1 h-11 font-medium rounded-xl transition-all ${
                quickAddSelectedIndex === 0 
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white ring-2 ring-indigo-500 dark:ring-indigo-400 ring-offset-2 scale-[1.02] shadow-lg shadow-indigo-500/30' 
                  : 'bg-slate-200 dark:bg-[#3e4145] hover:bg-slate-300 text-slate-600 dark:text-slate-300'
              }`}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Today
            </Button>
            <Button
              onClick={() => handleQuickAddTask(1)}
              disabled={!quickAddText.trim() || quickAddLoading}
              variant="outline"
              className={isStudioDesign()
                ? `flex-1 h-10 font-medium rounded-lg text-sm ${quickAddSelectedIndex === 1 ? 'border-foreground' : ''}`
                : `flex-1 h-11 font-medium rounded-xl transition-all ${
                quickAddSelectedIndex === 1 
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 ring-2 ring-indigo-500 dark:ring-indigo-400 ring-offset-2 scale-[1.02] shadow-lg' 
                  : 'border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/5 text-slate-600 dark:text-slate-300'
              }`}
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Tomorrow
            </Button>
          </div>
          <p className={isStudioDesign() ? 'text-[12.5px] text-slate-400 dark:text-slate-500 text-center mt-4' : 'text-xs text-slate-400 dark:text-slate-500 text-center mt-4'}>
            {isStudioDesign() ? 'Tab to switch · Enter or ⌘ Enter to add' : 'Tab to switch • Enter or ⌘/Ctrl+Enter to add'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );

  // Universal Delete Confirmation Dialog - works across ALL apps
  // Detect recurring tasks to show the 3-option delete prompt
  const isRecurringTaskDelete = itemToDelete?.type === 'task' &&
    (itemToDelete?.data?.is_recurring || itemToDelete?.data?.parent_task_id);

  const UniversalDeleteModal = (
    <Dialog open={showDeleteConfirm} onOpenChange={(open) => { setShowDeleteConfirm(open); if (!open) setItemToDelete(null); }}>
      <DialogContent className={isRecurringTaskDelete ? "max-w-md" : "sm:max-w-sm"} data-testid="universal-delete-dialog">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${isRecurringTaskDelete || isStudioDesign() ? '' : 'text-red-600 dark:text-red-400'}`}>
            {isRecurringTaskDelete ? (
              <>
                <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <span>Delete Recurring Task</span>
              </>
            ) : (
              <>
                {!isStudioDesign() && <Trash2 className="w-5 h-5" />}
                Delete?
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isRecurringTaskDelete
              ? 'This is a recurring task. How would you like to delete it?'
              : String(itemToDelete?.type || '').startsWith('shared_')
                ? 'This is a shared item. Deleting it removes it for everyone it is shared with, and it cannot be undone.'
                : 'Are you sure you want to delete this? This action cannot be undone.'}
          </DialogDescription>
        </DialogHeader>
        {isRecurringTaskDelete ? (
          <>
            <div className="space-y-2 py-4">
              <Button
                variant="outline"
                className="w-full justify-start h-auto p-4 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5"
                onClick={() => handleConfirmHoverDelete('single')}
                data-testid="hover-delete-single-btn"
              >
                <div className="text-left">
                  <div className="font-medium">Delete this occurrence only</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">Only this single instance will be deleted</div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start h-auto p-4 rounded-xl hover:bg-slate-50 dark:hover:bg-white/5"
                onClick={() => handleConfirmHoverDelete('future')}
                data-testid="hover-delete-future-btn"
              >
                <div className="text-left">
                  <div className="font-medium">Delete this and future occurrences</div>
                  <div className="text-sm text-slate-500 dark:text-slate-400">This and all future instances will be deleted</div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start h-auto p-4 rounded-xl border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-500/15"
                onClick={() => handleConfirmHoverDelete('all')}
                data-testid="hover-delete-all-btn"
              >
                <div className="text-left">
                  <div className="font-medium text-red-600 dark:text-red-400">Delete all occurrences</div>
                  <div className="text-sm text-red-400">All instances of this recurring task will be deleted</div>
                </div>
              </Button>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { setShowDeleteConfirm(false); setItemToDelete(null); }}>Cancel</Button>
            </DialogFooter>
          </>
        ) : (
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant={isStudioDesign() ? 'ghost' : 'outline'} onClick={() => { setShowDeleteConfirm(false); setItemToDelete(null); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmHoverDelete}
              autoFocus
              className="focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:ring-offset-2"
            >
              Delete
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );

  const QuickComposeModalGlobal = (
    <QuickComposeModal
      open={showQuickCompose}
      onClose={() => {
        setShowQuickCompose(false);
        setPcoServicePlan(null);
      }}
      user={user}
      pcoTeamMembers={pcoServicePlan?.team_members || null}
      directRoster={pcoServicePlan?.direct_roster ? pcoServicePlan : null}
      groupName={pcoServicePlan?.group_name || null}
      planUrl={pcoServicePlan?.plan_url || null}
      initialBody={pcoServicePlan?.direct_roster ? (pcoServicePlan.body || null) : null}
      rightSidebar={pcoServicePlan ? <ServiceDetailFloater plan={pcoServicePlan} /> : null}
    />
  );

  if (currentApp === 'planner') {
    return (
      <div data-section="planner">
        <Suspense fallback={<LoadingSpinner />}>
          <PlannerApp 
            user={user} 
            onBack={() => setCurrentApp(null)} 
            initialView={plannerView}
            globalTasks={globalTasks}
            globalPriorities={globalPriorities}
            globalGoals={globalGoals}
            setGlobalTasks={setGlobalTasks}
            setGlobalPriorities={setGlobalPriorities}
            setGlobalGoals={setGlobalGoals}
            setHoveredItem={setHoveredItem}
          />
        </Suspense>
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
        <FloatingThemeToggle />
      </div>
    );
  }

  if (currentApp === 'services') {
    return (
      <div data-section="services">
        <Suspense fallback={<LoadingSpinner />}>
          <ServicesApp user={user} onBack={() => { setCurrentApp(null); if (window.location.pathname === '/services') window.history.replaceState({}, '', '/'); }} initial={servicesInitial} onOpenSettings={handleOpenSettings} setShowQuickCompose={setShowQuickCompose} setPcoServicePlan={setPcoServicePlan} notifStamp={servicesNotifStamp} notificationHook={notificationHook} />
        </Suspense>
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
        <FloatingThemeToggle />
      </div>
    );
  }

  if (currentApp === 'aichat') {
    return (
      <div data-section="ai">
        <KeepAliveApps 
          user={user}
          activeApp="aichat"
          onboardingProps={{
            user,
            onBack: () => setCurrentApp(null),
            highlightCandidateId: onboardingHighlightCandidateId,
            onClearHighlight: () => setOnboardingHighlightCandidateId(null),
            initialData: globalOnboardingData
          }}
          worshipAIProps={{
            user,
            onBack: () => { setCurrentApp(null); setInitialAIMessage(''); setAutoSubmitAI(false); setInitialAIConversationId(null); },
            initialMessage: initialAIMessage,
            autoSubmit: autoSubmitAI,
            initialConversationId: initialAIConversationId
          }}
        />
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
        <FloatingThemeToggle />
      </div>
    );
  }

  if (currentApp === 'comms') {
    return (
      <div data-section="comms">
        <Suspense fallback={<LoadingSpinner />}>
          <TeamCommsApp 
            user={user} 
            onBack={() => { setCurrentApp(null); setCommsHighlightPostId(null); setCommsHighlightCommentId(null); setCommsInitialTab(null); setCommsInitialConversationId(null); setCommsHighlightMessageId(null); setCommsServiceHighlight(null); }} 
            highlightPostId={commsHighlightPostId}
            highlightCommentId={commsHighlightCommentId}
            globalPosts={globalPosts}
            setGlobalPosts={setGlobalPosts}
            setHoveredItem={setHoveredItem}
            onUpdateUser={handleUpdateUser}
            initialView={commsInitialTab || 'feed'}
            initialConversationId={commsInitialConversationId}
            initialMessageId={commsHighlightMessageId}
            serviceHighlight={commsServiceHighlight}
            notificationHook={notificationHook}
            markMessagesAsRead={markMessagesAsRead}
            archivedConversationIds={archivedConversationIds}
          />
        </Suspense>
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
        <FloatingThemeToggle />
      </div>
    );
  }

  if (currentApp === 'onboarding') {
    return (
      <div data-section="settings">
        <KeepAliveApps 
          user={user}
          activeApp="onboarding"
          onboardingProps={{
            user,
            onBack: () => setCurrentApp(null),
            highlightCandidateId: onboardingHighlightCandidateId,
            onClearHighlight: () => setOnboardingHighlightCandidateId(null),
            initialData: globalOnboardingData
          }}
          worshipAIProps={{
            user,
            onBack: () => { setCurrentApp(null); setInitialAIMessage(''); setAutoSubmitAI(false); setInitialAIConversationId(null); },
            initialMessage: initialAIMessage,
            autoSubmit: autoSubmitAI,
            initialConversationId: initialAIConversationId
          }}
        />
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
        <FloatingThemeToggle />
      </div>
    );
  }

  if (currentApp === 'tutorials') {
    return (
      <div data-section="tutorials">
        <Suspense fallback={<LoadingSpinner />}>
          <TutorialVideosPage onBack={() => {
            if (window.location.pathname.startsWith('/tutorials')) {
              window.history.replaceState({}, '', '/');
            }
            setCurrentApp(null);
          }} />
        </Suspense>
        <FloatingThemeToggle />
      </div>
    );
  }

  if (currentApp === 'messaging') {
    return (
      <div data-section="messaging">
        <Suspense fallback={<LoadingSpinner />}>
          <MessagingApp 
            user={user} 
            onBack={() => setCurrentApp(null)} 
          />
        </Suspense>
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
        <FloatingThemeToggle />
      </div>
    );
  }

  // Settings Pages
  if (currentSettings === 'profile') {
    return (
      <>
        <Suspense fallback={<LoadingSpinner />}>
          <ProfilePage user={user} onBack={handleCloseSettings} onUpdateUser={handleUpdateUser} />
        </Suspense>
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
      </>
    );
  }

  if (currentSettings === 'billing') {
    return (
      <>
        <Suspense fallback={<LoadingSpinner />}>
          <BillingPage user={user} onBack={handleCloseSettings} onNavigateToTeam={() => setCurrentSettings('team')} />
        </Suspense>
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
      </>
    );
  }

  if (currentSettings === 'calendar') {
    return (
      <>
        <Suspense fallback={<LoadingSpinner />}>
          <CalendarSyncPage user={user} onBack={handleCloseSettings} />
        </Suspense>
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
      </>
    );
  }

  if (currentSettings === 'team') {
    return (
      <>
        <Suspense fallback={<LoadingSpinner />}>
          <TeamManagementPage user={user} onBack={handleCloseSettings} onOpenSettings={handleOpenSettings} onUpdateUser={handleUpdateUser} />
        </Suspense>
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
      </>
    );
  }

  if (currentSettings === 'notifications') {
    return (
      <>
        <Suspense fallback={<LoadingSpinner />}>
          <NotificationSettings user={user} onBack={handleCloseSettings} />
        </Suspense>
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
      </>
    );
  }

  if (currentSettings === 'app-stats') {
    return (
      <>
        <Suspense fallback={<LoadingSpinner />}>
          <AppStatsDashboard user={user} onBack={handleCloseSettings} />
        </Suspense>
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
      </>
    );
  }

  if (currentSettings === 'planning-center') {
    return (
      <>
        <Suspense fallback={<LoadingSpinner />}>
          <PlanningCenterSettingsPage user={user} onBack={handleCloseSettings} />
        </Suspense>
        {QuickAddModal}
        {QuickComposeModalGlobal}
        {UniversalDeleteModal}
      </>
    );
  }

  return (
    <div data-section="dashboard">
      <Dashboard 
        user={user} 
        onSignOut={handleSignOut} 
        onOpenApp={handleOpenApp} 
        onOpenSettings={handleOpenSettings} 
        currentPlan={userPlan} 
        setHoveredItem={setHoveredItem}
        globalTasks={globalTasks}
        setGlobalTasks={setGlobalTasks}
        globalRecentComms={globalRecentComms}
        archivedConversationIds={archivedConversationIds}
        notificationHook={notificationHook}
        markMessagesAsRead={markMessagesAsRead}
        pendingOnboardingNotifsRef={pendingOnboardingNotifsRef}
        setShowQuickCompose={setShowQuickCompose}
        setShowQuickAdd={setShowQuickAdd}
        setPcoServicePlan={setPcoServicePlan}
      />
      {/* Prefetch JS chunks in background for instant navigation */}
      <PrefetchComponents user={user} />
      {QuickAddModal}
      {QuickComposeModalGlobal}
      {UniversalDeleteModal}
      <FloatingThemeToggle />
      
      {/* Planning Center Post-Connection Prompt */}
      <Dialog open={showPCConnectedPrompt} onOpenChange={setShowPCConnectedPrompt}>
        <DialogContent className="max-w-sm" data-testid="pc-connected-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg>
              Planning Center Connected!
            </DialogTitle>
            <DialogDescription>
              Would you like to invite your team members from Planning Center to Worship OS?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowPCConnectedPrompt(false)} data-testid="pc-prompt-later-btn">
              Later
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={() => {
                setShowPCConnectedPrompt(false);
                setCurrentSettings('team');
              }}
              data-testid="pc-prompt-invite-btn"
            >
              Yes, Invite Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AnnouncementPopup userId={user?.id} userRole={user?.role} />
    </div>
  );
}

// Wrap App with providers
function AppWithProviders() {
  // Studio puts toasts bottom-right with no rich colours and no close button
  // (docs/app-map/101 §6); classic keeps the top-centre rich toast.
  const { isStudio } = useDesign();
  return (
    <AvatarCacheProvider>
      <App />
      <ConnectionBanner />
      {isStudio ? (
        <Toaster position="bottom-right" richColors={false} closeButton={false} />
      ) : (
        <Toaster position="top-center" richColors closeButton offset="50px" />
      )}
    </AvatarCacheProvider>
  );
}

export default AppWithProviders;
